import { isInertLink, isNestedLink } from "../../../engine/sync/link-state.js";
import { createTaskFilter, setTaskFilter } from '../ui/note-task-filter.js';
import { mountMobile as mountRoutines, hidePanel as hideRoutinesPanel } from '../ui/routines.js';
let mobileRoutinesController=null;
import { bindMobileNoteSearch } from './notes-search.js';
import { readSearchableNote } from '../views/notes/search.js';
import { isDocQuotaBlocked } from "../../../engine/services/quota-state.js";
import { inProgressTags } from '../../../engine/core/node-tags.js';
import { normalizeEstimate } from '../../../engine/core/estimate.js';
/**
 * Mobile app shell for Orbital.
 *
 * This layer owns mobile navigation and rendering only. It reuses the
 * production Store/CRDT/vault callbacks passed from main.js.
 */

import { getSyncPauses } from '../../../engine/sync/sync-pause.js';
import { updateDocumentSizes, loadStorageSummary } from '../ui/storage-usage.js';
import { on, Events } from "../../../engine/core/events.js";
import { handleMobileActionClick } from "./actions/click-actions.js";
import { createDocActions } from "./actions/doc-actions.js";
import {
  handleMobileInput,
  handleMobileRenameBlur,
  handleMobileRenameKeydown,
} from "./actions/input-actions.js";
import {
  clearMemoryStashes,
  configureRecoveryStash,
  dropStashEntriesForDoc as dropRecoveryStashEntriesForDoc,
  peekStashedNodeEdit,
  peekStashedNoteEdit,
  pruneExpiredStashes,
  stashUnsavedNodeEdit,
  stashUnsavedNoteEdit,
  takeStashedNodeEdit,
  takeStashedNoteEdit,
} from "./actions/recovery-stash.js";
import {
  commitPendingDesktopViewTransform,
  configureDesktopPreviewTouch,
  handleDesktopViewTouchEnd,
  handleDesktopViewTouchMove,
  handleDesktopViewTouchStart,
  resetDesktopViewTouchState,
} from "./gestures/desktop-preview-touch.js";
import {
  configureDesktopPreviewMount,
  destroyDesktopPreviewMount,
  detachMountedDesktopViewToOriginalParent,
  fitMountedDesktopView,
  getMountedDesktopViewName,
  syncDesktopPreviewMount,
} from "./gestures/desktop-preview-mount.js";
import {
  configureMobileNoteEditorMount,
  detachMobileNoteEditor,
  destroyMobileNoteEditor,
  syncMobileNoteEditorMount,
} from "./note-editor-mount.js";
import {
  configureMobileAttachments,
  destroyMobileAttachments,
} from "./attachments.js";
import {
  configureGanttPinch,
  handleGanttPinchEnd,
  handleGanttPinchMove,
  handleGanttPinchStart,
  resetGanttPinch,
} from "./gestures/gantt-pinch.js";
import {
  configureOutlineTouch,
  handleSwipeCancel,
  handleSwipeEnd,
  handleSwipeMove,
  handleSwipeStart,
  resetOutlineTouch,
} from "./gestures/outline-touch.js";
import {
  configurePullRefresh,
  handlePullEnd,
  handlePullMove,
  handlePullStart,
  resetPullRefresh,
} from "./gestures/pull-refresh.js";
import {
  GANTT_ROW_HEIGHT_MAX,
  GANTT_ROW_HEIGHT_MIN,
  PERSISTENT_FOCUS_IDS,
} from "./constants.js";
import { attachGanttScroll, ganttCenterDay, ganttRowAnchor } from "./gestures/gantt-scroll.js";
import { captureMobileExecutionScroll, restoreMobileExecutionScroll, bindMobileExecutionReorder } from "./gestures/execution-list.js";
import { renderHorizonControl } from "../ui/horizon-control.js";
import { escapeAttr, escapeHtml } from "./utils/html.js";
import { MOBILE_SHELL_MEDIA_QUERY } from "../../../shared/utils/device-detect.js";
import { collectDatedEntries, collectLeafTasks, collectTreeEntries } from "./utils/tree.js";
import { renderBoard } from "./render/board.js";
import { renderDocs } from "./render/docs.js";
import { renderEmpty } from "./render/empty.js";
import { renderMapView } from "./render/mapview.js";
import { renderNodePanel } from "./render/node-panel.js";
import { renderNotes } from "./render/notes.js";
import {
  computeWindow,
  flattenVisibleRows,
  getRowHeight,
  measureRowHeight,
  renderOutline,
  renderRowWindow,
} from "./render/outline.js";
import {
  renderConfirmSheet,
  renderDocActionsSheet,
  renderFilterSheet,
  renderFolderActionsSheet,
  renderFormSheet,
  renderMoveDocSheet,
  renderMoveSheet,
  renderSearchSheet,
  renderStatusSheet,
  renderDocPropertiesSheet,
  renderNotificationsSheet,
  renderUserInfoSheet,
} from "./render/sheets.js";
import { renderMobileError, renderMobileShell } from "./render/shell.js";
import { renderTimeline, redrawGanttRows } from "./render/timeline.js";
import { toDateInputValue } from "./utils/dates.js";
import { noteEditUpdates } from "./utils/note-edit.js";
import { t } from "../../../shared/i18n/index.js";
import { hintText } from "../../../engine/core/server-status.js";
import { mapStorageOf } from "../ui/map-storage.js";
import { getServerStatus } from "../managers/sync-orchestrator.js";
import { tagLabel } from "../../../engine/core/tag-filter.js";

let container = null;
let callbacks = null;
let initialized = false;
let storeUnsubscribe = null;
let installPromptEvent = null;
let installPromptHandler = null;
let visibilityChangeHandler = null;
let syncActiveUntil = 0;
let lastServerStatus = null; // header server indicator (Events.SERVER_STATUS_CHANGED)
let syncClearTimer = null;
let eventUnsubscribers = [];
let visibilityObserver = null;
let renderQueued = false;
let renderingInProgress = false;
let focusAfterRender = null;
let lastDocId = null;

const uiState = {
  tab: "outline",
  activeNodeId: null,
  nodeEditing: false,
  filterOpen: false,
  activeNoteId: null,
  activeNoteDocId: null,
  noteEditing: false,
  boardColumnIndex: 0,
  docsFolderId: null,
  docsSearchQuery: "",
  notesSearchQuery: "",
  searchOpen: false,
  searchQuery: "",
  userInfoOpen: false,
  docPropsOpen: false,
  outlineScrollTop: 0,
  outlineViewportH: 0,
  notesListScrollLeft: 0,
  canInstall: false,
  form: null,
  confirm: null,
  moveNodeId: null,
  moveDocId: null,
  docActionsId: null,
  folderActionsId: null,
  statusNodeId: null,
  statusSourceTagId: null,
  expandedBoardNodeId: null,
  editingTitleNodeId: null,
  // Edit-time docId+id snapshots — guard saves against doc-switch races so a
  // late save can't overwrite the wrong note/node in a different doc.
  editingNoteContext: null, // { docId, noteId }
  editingNodeContext: null, // { docId, nodeId }
  inlineRenameContext: null, // { docId, nodeId }
  noteEditDraft: null, // { docId, noteId, title, content }
  nodeEditDraft: null, // { docId, nodeId, name, startDate, endDate, notes, color }
  inlineRenameDraft: null, // { docId, nodeId, name }
  // Form sheet drafts. Keyed by form type so a doc-switch render of an open
  // create/rename/add-node form preserves typed text instead of resetting
  // the value attribute on every Store push. { type, fields: {id: value} }
  formDraft: null,
  // One-shot payload populated after the user accepts a stashed-edit recovery
  // confirm. The next renderActiveNote/renderNodePanel uses these as the input
  // defaults so the recovery survives the post-confirm re-render. Cleared
  // after consumption.
  recoveredEditPayload: null,
  ganttZoomIndex: 2, // index into GANTT_ZOOM_LEVELS
  ganttRowHeight: 44, // pixel height per gantt row, pinchable
  todayDrafts: new Map(), // unfinished task names, scoped to notes in the current doc
};

const docActions = createDocActions({
  getCallbacks: () => callbacks,
  uiState,
  ensurePendingEditsSavedBeforeNavigation,
  scheduleRender,
  showMobileError,
});

export function openVault() {
  if (!callbacks || !ensurePendingEditsSavedBeforeNavigation()) return;
  if (uiState.tab !== 'docs') uiState.lastContentTab = uiState.tab;
  uiState.tab = 'docs';
  closeTransientSheets();
  scheduleRender();
}

export function init(cbs) {
  callbacks = cbs;
  container = document.getElementById("mobile-app");
  if (!container) {
    console.warn("[Mobile] #mobile-app not found");
    return;
  }

  // Re-bind dependency injection on every init so re-mounts with potentially
  // different callback shapes pick up the latest bindings even when
  // initialized=true. Idempotent — each configure* overwrites its own state.
  configureRecoveryStash({ getCallbacks: () => callbacks });
  configurePullRefresh({ getCallbacks: () => callbacks, scheduleRender });
  configureGanttPinch({ uiState, scheduleRender });
  configureDesktopPreviewMount({
    getCallbacks: () => callbacks,
    getContainer: () => container,
    uiState,
  });
  configureMobileNoteEditorMount({
    getCallbacks: () => callbacks,
  });
  configureMobileAttachments({
    // Attachments belong to the doc that owns the note being edited — not
    // necessarily the currently-open doc, if a note panel outlived a switch.
    getDocId: () =>
      uiState.editingNoteContext?.docId ||
      callbacks.Store.getState()?.currentDocId ||
      callbacks.VaultStorage?.getCurrentDocId?.() ||
      null,
    showToast: (msg, opts) => callbacks.showToast?.(msg, opts),
    scheduleRender,
  });
  configureDesktopPreviewTouch({
    getCallbacks: () => callbacks,
    getMountedViewName: getMountedDesktopViewName,
    setSuppressNextRowClick: (value) => { suppressNextRowClick = value; },
    scheduleRender,
    uiState,
  });
  configureOutlineTouch({
    getCallbacks: () => callbacks,
    openAddNodeAtRoot,
    openNode,
    requestDeleteNode,
    setSuppressNextRowClick: (value) => { suppressNextRowClick = value; },
  });

  if (initialized) {
    scheduleRender();
    return;
  }

  initialized = true;
  resetTransientUi();
  pruneExpiredStashes();
  if (callbacks.getCurrentUser?.()) clearMemoryStashes();
  // Viewport flips INTO mobile range (rotate, window resize): the shell markup
  // may be stale from before the flip and its desktop-view mount was released
  // to #app — force a real re-render so the map tab re-embeds its view.
  window.matchMedia(MOBILE_SHELL_MEDIA_QUERY).addEventListener("change", (e) => {
    if (!e.matches) return;
    lastRenderedHtml = null;
    lastShellParts = null;
    scheduleRender();
  });
  container.addEventListener("click", handleClick);
  container.addEventListener("input", handleInput);
  container.addEventListener("keydown", handleKeydown);
  container.addEventListener("blur", handleBlur, true);
  container.addEventListener("touchstart", handleSwipeStart, { passive: true });
  container.addEventListener("touchmove", handleSwipeMove, { passive: false });
  container.addEventListener("touchend", handleSwipeEnd);
  container.addEventListener("touchcancel", handleSwipeCancel);
  container.addEventListener("touchstart", handleDesktopViewTouchStart, { passive: true });
  container.addEventListener("touchmove", handleDesktopViewTouchMove, { passive: false });
  container.addEventListener("touchend", handleDesktopViewTouchEnd);
  container.addEventListener("touchcancel", handleDesktopViewTouchEnd);
  container.addEventListener("touchstart", handleGanttPinchStart, { passive: true });
  container.addEventListener("touchmove", handleGanttPinchMove, { passive: false });
  container.addEventListener("touchend", handleGanttPinchEnd);
  container.addEventListener("touchcancel", handleGanttPinchEnd);
  container.addEventListener("touchstart", handlePullStart, { passive: true });
  container.addEventListener("touchmove", handlePullMove, { passive: false });
  container.addEventListener("touchend", handlePullEnd);
  container.addEventListener("touchcancel", handlePullEnd);

  installPromptHandler = (event) => {
    event.preventDefault();
    installPromptEvent = event;
    scheduleRender();
  };
  window.addEventListener("beforeinstallprompt", installPromptHandler);

  // If the OS interrupts a desktop-view pinch/pan (system gesture, tab
  // backgrounded), commit whatever transform we last applied so the desktop
  // view doesn't keep an uncommitted live transform that would surface as
  // stale state next time the user opens it on desktop.
  visibilityChangeHandler = () => {
    if (document.visibilityState === "hidden") {
      commitPendingDesktopViewTransform();
    }
  };
  document.addEventListener("visibilitychange", visibilityChangeHandler);
  // Document-level: Tab can move focus out of the mobile container entirely,
  // so the dialog trap cannot be bound to the container.
  document.addEventListener("keydown", handleDialogKeydown, true);

  setupKeyboardInsetTracking();

  storeUnsubscribe = callbacks.Store.subscribe(() => scheduleRender());
  lastServerStatus = getServerStatus();
  const flagSyncActive = () => {
    syncActiveUntil = Date.now() + 800;
    clearTimeout(syncClearTimer);
    syncClearTimer = setTimeout(() => scheduleRender(), 850);
    scheduleRender();
  };
  eventUnsubscribers = [
    on(Events.DOC_NOTES_CHANGED, () => scheduleRender()),
    on(Events.EXECUTION_CHANGED, () => { executionRevision++; scheduleRender(); }),
    on(Events.ROUTINES_CHANGED, () => scheduleRender()),
    on(Events.VAULT_INDEX_UPDATED, () => scheduleRender()),
    on(Events.SERVER_STATUS_CHANGED, (status) => {
      if (lastServerStatus?.level === status?.level && lastServerStatus?.text === status?.text) return;
      lastServerStatus = status;
      scheduleRender();
    }),
    // Share invitations arrive over the user-WS at any time; the Docs tab
    // renders them from getInvitations(), so it has to repaint on each change.
    on(Events.INVITATIONS_UPDATED, () => scheduleRender()),
    on(Events.INVITATION_ACCEPTED, () => scheduleRender()),
    on(Events.INVITATION_DECLINED, () => scheduleRender()),
    on(Events.USER_CRDT_UPDATED, () => scheduleRender()),
    on(Events.SYNC_CONNECTED, () => { flagSyncActive(); scheduleRender(); }),
    on(Events.SYNC_DISCONNECTED, () => scheduleRender()),
    on(Events.SYNC_PAUSED, () => scheduleRender()),
    on(Events.SYNC_PAUSE_CHANGED, () => scheduleRender()),
    on(Events.QUOTA_CHANGED, () => scheduleRender()),
    on(Events.SYNC_REMOTE_UPDATE, () => flagSyncActive()),
    on(Events.SAVE_STATUS, ({ status } = {}) => {
      if (status === "saving") flagSyncActive();
    }),
    on(Events.AUTH_LOGIN, () => {
      clearMemoryStashes();
    }),
    // Drop account draft state on logout, retaining durable local recovery
    // stashes. Account-specific persisted entries are removed by AuthManager.
    // Clear in-flight draft state so we
    // don't leak across users on a shared device. Without dropping the
    // in-flight drafts, the post-logout doc-switch render would call
    // flushPendingNoteEditSaveTo, fail to resolve the old doc, and write a
    // brand-new account draft after the cleanup.
    on(Events.AUTH_LOGOUT, () => {
      uiState.noteEditing = false;
      uiState.nodeEditing = false;
      uiState.editingNoteContext = null;
      uiState.editingNodeContext = null;
      uiState.inlineRenameContext = null;
      // Tear the collab note editor down explicitly: AUTH_LOGOUT doesn't
      // schedule a render here, so the post-render hook that would
      // normally destroy a stale editor never runs. Without this call
      // the editor + its host element would survive the logout and
      // leak into the next session.
      destroyMobileNoteEditor();
      uiState.noteEditDraft = null;
      uiState.nodeEditDraft = null;
      uiState.inlineRenameDraft = null;
      uiState.editingTitleNodeId = null;
      uiState.recoveredEditPayload = null;
      uiState.formDraft = null;
      uiState.userInfoOpen = false;
      clearMemoryStashes();
    }),
  ];

  setupVisibilityObserver();
  render();
}

// Drop stash entries (memory + localStorage) for a doc that's been deleted.
// Logged-in users have stashes ONLY in memory, so the localStorage-only
// scrub in main.js's dropMobileStashEntriesForDoc would leak ghost
// recoveries on the next edit of any node/note that happens to share an
// id with the deleted-doc's notes/nodes. Callers in main.js use this
// before / after deletion through callbacks.
/**
 * Give the embedded desktop view element back to #app. Called by main.js when
 * the viewport flips to desktop width: the mobile shell only re-renders on its
 * own events, so without this the mindmap/bubble element would stay captive
 * inside the hidden shell and the desktop canvas would be empty.
 */
export function releaseEmbeddedDesktopView() {
  detachMountedDesktopViewToOriginalParent();
  // The shell's markup still describes a mounted map host, so a later render
  // could match the cached string, take the byte-identical skip, and never
  // remount. Drop the cache: the next render does a real write, whose
  // post-render hook re-embeds the view if the user is back on a map tab.
  lastRenderedHtml = null;
  lastShellParts = null;
}

export function dropStashEntriesForDoc(docId) {
  dropRecoveryStashEntriesForDoc(docId);
}

/**
 * Open the node properties panel for a node. Used by the map canvas
 * renderers' long-press gesture (outline rows already long-press via
 * gestures/outline-touch.js → openNode).
 */
export function openNodeProperties(nodeId) {
  if (!initialized) return;
  openNode(nodeId);
}

export function openRelatedNotes(nodeId) {
  if (!initialized || !ensurePendingEditsSavedBeforeNavigation()) return;
  const task = createTaskFilter(nodeId, callbacks.sharedCallbacks);
  if (!task) return;
  uiState.activeNodeId = null;
  uiState.nodeEditing = false;
  uiState.editingNodeContext = null;
  clearNodeEditDraft();
  uiState.noteEditing = false;
  uiState.editingNoteContext = null;
  clearNoteEditDraft();
  closeTransientSheets();
  uiState.tab = 'notes';
  uiState.notesSearchQuery = '';
  uiState.noteSearchMatch = null;
  uiState.noteSearchReveal = true;
  setTaskFilter(task, true);
  scheduleRender();
}

let mobileExecutionCleanup = null;
let mobileNoteSearchCleanup = null;

export function destroy() {
  if (!initialized) return;
  initialized = false;
  mobileExecutionCleanup?.();
  mobileNoteSearchCleanup?.();
  mobileExecutionCleanup = null;
  mobileNoteSearchCleanup = null;
  container?.removeEventListener("click", handleClick);
  container?.removeEventListener("input", handleInput);
  container?.removeEventListener("keydown", handleKeydown);
  document.removeEventListener("keydown", handleDialogKeydown, true);
  container?.removeEventListener("blur", handleBlur, true);
  container?.removeEventListener("touchstart", handleSwipeStart);
  container?.removeEventListener("touchmove", handleSwipeMove);
  container?.removeEventListener("touchend", handleSwipeEnd);
  container?.removeEventListener("touchcancel", handleSwipeCancel);
  container?.removeEventListener("touchstart", handleDesktopViewTouchStart);
  container?.removeEventListener("touchmove", handleDesktopViewTouchMove);
  container?.removeEventListener("touchend", handleDesktopViewTouchEnd);
  container?.removeEventListener("touchcancel", handleDesktopViewTouchEnd);
  container?.removeEventListener("touchstart", handleGanttPinchStart);
  container?.removeEventListener("touchmove", handleGanttPinchMove);
  container?.removeEventListener("touchend", handleGanttPinchEnd);
  container?.removeEventListener("touchcancel", handleGanttPinchEnd);
  container?.removeEventListener("touchstart", handlePullStart);
  container?.removeEventListener("touchmove", handlePullMove);
  container?.removeEventListener("touchend", handlePullEnd);
  container?.removeEventListener("touchcancel", handlePullEnd);
  if (installPromptHandler) {
    window.removeEventListener("beforeinstallprompt", installPromptHandler);
    installPromptHandler = null;
  }
  if (visibilityChangeHandler) {
    document.removeEventListener("visibilitychange", visibilityChangeHandler);
    visibilityChangeHandler = null;
  }
  installPromptEvent = null;
  teardownKeyboardInsetTracking();
  destroyDesktopPreviewMount();
  storeUnsubscribe?.();
  storeUnsubscribe = null;
  eventUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
  eventUnsubscribers = [];
  visibilityObserver?.disconnect();
  visibilityObserver = null;
  // Cancel any pending timers so they don't fire against a torn-down shell.
  if (syncClearTimer) {
    clearTimeout(syncClearTimer);
    syncClearTimer = null;
  }
  resetPullRefresh();
  resetOutlineTouch();
  resetGanttPinch();
  resetDesktopViewTouchState();
  // Drop in-memory stash mirrors so a re-init doesn't see ghost recovery
  // payloads left over from the previous mount.
  clearMemoryStashes();
  // Reset module-scoped render-tracking state so a fresh init doesn't compare
  // against the previous mount's last-seen doc id or panel signature.
  lastDocId = null;
  previousPanelSignature = "";
  focusAfterRender = null;
  renderQueued = false;
  renderingInProgress = false;
  pendingGanttCenterDay = null;
  pendingScrollPin = null;
  document.body.classList.remove("mobile-shell-active");
  if (container) container.innerHTML = "";
  // The render cache mirrors what is mounted in the container. We just wiped
  // it, so drop the cache too — otherwise the first render after a re-init
  // could match the stale string and skip, leaving an empty shell.
  lastRenderedHtml = null;
  lastShellParts = null;
  // The attachment pickers live on document.body (outside the container), so
  // wiping the container does not remove them.
  destroyMobileAttachments();
  // Drop top-level references after listeners and timers are gone. Any future
  // gesture handler that races a destroy reads these via closures and bails
  // safely on the null check (render() already guards this).
  callbacks = null;
  container = null;
}

function setupVisibilityObserver() {
  syncVisibility(false);
  const mainApp = document.getElementById("main-app");
  if (!mainApp) return;
  visibilityObserver = new MutationObserver(() => syncVisibility());
  visibilityObserver.observe(mainApp, {
    attributes: true,
    attributeFilter: ["class"],
  });
}

function syncVisibility(hasRenderedShell = !!container?.querySelector(".mobile-shell")) {
  const mainApp = document.getElementById("main-app");
  const active = !!mainApp && !mainApp.classList.contains("hidden") && hasRenderedShell;
  document.body.classList.toggle("mobile-shell-active", active);
}

function scheduleRender(focusSelector = null) {
  if (focusSelector) {
    focusAfterRender = focusSelector;
  }
  if (renderQueued) return;
  // If we're currently inside a render() call, a synchronous CRDT write or
  // sub-callback may have triggered scheduleRender. Defer to a microtask
  // so the current render finishes first, then we coalesce.
  if (renderingInProgress) {
    renderQueued = true;
    queueMicrotask(() => {
      // Hand off to a real rAF so we don't double-schedule.
      requestAnimationFrame(() => {
        renderQueued = false;
        render();
        restoreFocusAfterRender();
      });
    });
    return;
  }
  renderQueued = true;
  requestAnimationFrame(() => {
    renderQueued = false;
    render();
    restoreFocusAfterRender();
  });
}

function restoreFocusAfterRender() {
  if (!focusAfterRender) return;
  // focusAfterRender can be either a CSS selector (legacy) or an object
  // capturing selector + selection range so the caret position survives.
  const target = focusAfterRender;
  focusAfterRender = null;
  const selector = typeof target === "string" ? target : target.selector;
  const el = container?.querySelector(selector);
  if (!el) return;
  el.focus({ preventScroll: true });
  if (typeof el.setSelectionRange === "function") {
    const end = String(el.value || "").length;
    if (typeof target === "object" && target.selectionStart != null && target.selectionEnd != null) {
      const start = Math.min(end, Math.max(0, target.selectionStart));
      const finish = Math.min(end, Math.max(start, target.selectionEnd));
      try {
        el.setSelectionRange(start, finish);
      } catch (e) {
        el.setSelectionRange(end, end);
      }
    } else if (el.matches?.("[data-mobile-rename-input]")) {
      el.setSelectionRange(0, end);
    } else {
      el.setSelectionRange(end, end);
    }
  }
}

function captureActiveEditorFocus() {
  if (focusAfterRender) return; // explicit set already pending — don't override
  const active = document.activeElement;
  if (!active || !container?.contains(active)) return;
  const id = active.id;
  let selector = null;
  if (id && PERSISTENT_FOCUS_IDS.has(id)) {
    selector = `#${cssEscape(id)}`;
  } else if (active.matches?.("[data-mobile-rename-input]") && active.dataset?.nodeId) {
    selector = `[data-mobile-rename-input][data-node-id="${cssEscape(active.dataset.nodeId)}"]`;
  }
  if (!selector) return;
  let selectionStart = null;
  let selectionEnd = null;
  try {
    if (typeof active.selectionStart === "number") {
      selectionStart = active.selectionStart;
      selectionEnd = active.selectionEnd ?? selectionStart;
    }
  } catch (e) { /* some inputs (date) don't support selection */ }
  focusAfterRender = { selector, selectionStart, selectionEnd };
}

function render() {
  if (!container || !callbacks) return;
  // Re-entrancy guard: a CRDT write inside render() (e.g. doc-switch flush)
  // emits events that loop back to scheduleRender. The renderingInProgress
  // flag tells scheduleRender to defer rather than synchronously re-enter.
  renderingInProgress = true;
  try {
    return _renderInternal();
  } finally {
    renderingInProgress = false;
  }
}

function _renderInternal() {
  // NOTE ON ORDERING: the markup is built first and only then compared against
  // what is already mounted (see the skip below). Nothing may touch the DOM
  // before that decision — detaching the desktop view or the note editor and
  // then deciding not to re-render would leave them orphaned. The focus
  // capture and the detach dance therefore live after the comparison, still
  // ahead of the innerHTML write.
  const appState = callbacks.Store.getState();
  const currentDocId = appState.currentDocId || callbacks.VaultStorage?.getCurrentDocId?.() || null;
  const documentChanged = lastDocId !== currentDocId;
  if (documentChanged) {
    // Flush any in-progress edit BEFORE clearing context so the user doesn't
    // silently lose typing when a remote/refnote nav switches docs out from
    // under them. We commit to the OLD docId (lastDocId), not the new one.
    if (uiState.noteEditing && uiState.editingNoteContext?.docId === lastDocId) {
      flushPendingNoteEditSaveTo(lastDocId);
    }
    if (uiState.nodeEditing && uiState.editingNodeContext?.docId === lastDocId) {
      flushPendingNodeEditSaveTo(lastDocId);
    }
    lastDocId = currentDocId;
    resetTransientUi();
    uiState.ganttBounds = null;
    uiState.ganttCenterDay = null;
    uiState.ganttScrollTop = 0;
    uiState.ganttRowAnchor = null;
    pendingGanttScrollToToday = true;
    pendingGanttCenterDay = null;
  } else {
    snapshotActiveEditDrafts();
  }
  sanitizeUiState(appState);
  const docTitle = getCurrentDocTitle(appState) || appState.data?.name || "Orbital";
  const currentUser = callbacks.getCurrentUser?.();
  const readOnly = callbacks.Store.isReadOnly?.();
  const syncActive = Date.now() < syncActiveUntil;
  const syncPaused = getSyncPauses().some(pause => pause.scope === 'vault' || pause.id === currentDocId);
  // Under the title: where the map is stored; the dot is the server status.
  const server = lastServerStatus;
  const modeLabel = isDocQuotaBlocked(currentDocId) ? t("app.status.storageFull") : syncPaused ? t("mobile.mode.syncPaused") : readOnly
    ? t(callbacks.Store.isDeleteBlocked?.() ? "mobile.mode.viewOnly" : "mobile.mode.mapFull")
    : syncActive && currentUser
      ? t("mobile.mode.syncing")
      : mapStorageOf(currentDocId) === "local" ? t("app.mapStorage.device") : t("app.mapStorage.online");
  const serverLevel = currentUser ? server?.level || "busy" : "none";
  const serverHint = server ? hintText(server, t) : "";
  const filterCount =
    (appState.highlightedTagIds?.length || 0) +
    (appState.hiddenTagIds?.length || 0);
  let activeHtml = "";
  let overlayHtml = "";

  try {
    activeHtml = renderActiveTab(appState);
  } catch (err) {
    console.error("[Mobile] Render activeTab failed:", err);
    activeHtml = renderMobileError(err);
  }
  // Render overlays independently — a thrown sheet shouldn't take the others
  // (especially the recovery confirm) down with it. Each sheet is wrapped so
  // we never lose user-relevant state to a single rendering bug.
  uiState.canInstall = !!installPromptEvent;
  const sheetDeps = {
    callbacks,
    uiState,
    getTagCategories: () => callbacks.sharedCallbacks.getTagCategories?.() || [],
    getCurrentDocTitle,
    collectFolderEntries,
    collectTreeEntries,
    findDoc,
    findFolder,
    getBoardColumns,
    getMoveSuggestions,
    getNodeTagIds,
    getRootFolderId,
    getSafeStatus,
    getSearchResults,
    getTagDefinitions,
    isDescendantOf,
    isOwnerDoc,
    normalizeFolderId,
  };
  const sheets = [
    () => renderNodePanel(appState, {
      callbacks,
      uiState,
      getBoardColumns,
      getNodeColorPalette,
      getNodeEditDraft,
      getNodeTagIds,
      getNodeTagLabels,
      getSafeStatus,
      getTagDefinitions,
      renderTagChip,
      renderToggleTag,
      stashUnsavedNodeEdit,
    }),
    () => renderFilterSheet(appState, sheetDeps),
    () => renderSearchSheet(appState, sheetDeps),
    () => renderFormSheet(sheetDeps),
    () => renderConfirmSheet(sheetDeps),
    () => renderMoveSheet(appState, sheetDeps),
    () => renderDocActionsSheet(appState, sheetDeps),
    () => renderFolderActionsSheet(sheetDeps),
    () => renderMoveDocSheet(sheetDeps),
    () => renderStatusSheet(sheetDeps),
    () => renderUserInfoSheet(sheetDeps),
    () => renderDocPropertiesSheet(appState, sheetDeps),
    () => renderNotificationsSheet(sheetDeps),
  ];
  overlayHtml = sheets.map((fn) => {
    try { return fn(); } catch (err) {
      console.error("[Mobile] Sheet render failed:", err);
      return "";
    }
  }).join("");

  // Suppress sheet open-animations on re-renders that don't change which sheets
  // are visible (otherwise every Store change re-fires the slide-up).
  const panelSignature = computePanelSignature();
  const noAnim = panelSignature === previousPanelSignature;

  const shell = renderMobileShell(appState, {
    activeHtml,
    currentUser,
    docTitle,
    filterCount,
    modeLabel,
    noAnim,
    overlayHtml,
    readOnly,
    serverHint,
    serverLevel,
    syncActive,
  }, { callbacks, uiState });
  const html = shell.html;

  // Skip the DOM write when the markup is byte-identical to what is already
  // mounted. Every Store change and every remote sync tick schedules a render,
  // but many of them cannot affect the visible tab — e.g. a collaborator
  // editing node names while the user sits on the Docs tab. Re-parsing the
  // whole shell for those costs an innerHTML teardown, a full re-layout, and a
  // detach/re-mount of the desktop preview and the collab note editor.
  //
  // Byte-identical markup means the resulting DOM would be identical, so
  // skipping is behaviour-preserving — with one exception: the post-render
  // effects also service *pending scroll intents* (go-to-today, zoom
  // re-centre, scroll pin), which are set by a tap that may not change any
  // markup. Those must still run, so they veto the skip.
  // The gantt intents are only ever serviced while the timeline tab is
  // rendered, and `pendingGanttScrollToToday` starts life as `true` — so
  // testing them unconditionally would leave the veto permanently on for
  // anyone who has not visited the timeline, and nothing would ever skip.
  // Scope them to the tab that can actually consume them.
  const hasPendingScrollIntent =
    pendingScrollPin != null || uiState.noteSearchReveal ||
    (uiState.tab === "timeline" &&
      (pendingGanttScrollToToday || pendingGanttCenterDay != null));

  if (html === lastRenderedHtml && !documentChanged && !hasPendingScrollIntent) {
    // Nothing was detached (the detach happens below, only on a real write),
    // so the live desktop view and note editor stay mounted and untouched.
    previousPanelSignature = panelSignature;
    syncHorizonControl(appState);
    return;
  }

  // Top-bar-only fast path. The sync status lives up here and ticks whenever
  // sync events arrive — connect, disconnect, remote update, vault refresh —
  // none of which need touch the document body. Rewriting the whole shell for
  // those meant re-parsing and re-laying-out the entire outline: on an
  // 890-node document that is ~1.2 MB of markup, and it is what makes a big
  // document feel like it stalls at random moments rather than while editing.
  //
  // Only taken when everything below the bar is byte-identical, so the outline,
  // the mounted desktop view and the collab note editor are provably unaffected
  // — which is also why none of the detach/re-attach dance below is needed. The
  // bar holds only buttons, so there is no focus or caret to preserve either.
  if (
    lastShellParts
    && !documentChanged
    && !hasPendingScrollIntent
    && shell.rest === lastShellParts.rest
    && shell.shellClass === lastShellParts.shellClass
  ) {
    const bar = container.querySelector(".mobile-topbar");
    if (bar) {
      bar.outerHTML = shell.topbar;
      lastRenderedHtml = html;
      lastShellParts = shell;
      previousPanelSignature = panelSignature;
      syncHorizonControl(appState);
      return;
    }
    // No bar mounted (first paint, or a teardown raced us) — fall through to
    // the full write rather than guessing.
  }

  // Opening a sheet must not tear down the map beneath it. Preserve its DOM,
  // canvas, scroll position and gesture bindings when its rendered content
  // and tab are unchanged. Data/filters still render normally; the embedded
  // map also receives Store updates through main's existing view subscriber.
  // Keep note/routine editor lifecycle on the full-render path.
  if (lastShellParts && !documentChanged && !hasPendingScrollIntent
      && ["outline", "mindmap", "bubble", "board", "timeline"].includes(uiState.tab)
      && shell.activeHtml === lastShellParts.activeHtml
      && shell.navigation === lastShellParts.navigation) {
    const root = container.querySelector(".mobile-shell");
    const overlays = root?.querySelector(".mobile-overlays");
    const bar = root?.querySelector(".mobile-topbar");
    if (root && overlays && bar) {
      root.className = shell.shellClass;
      if (shell.topbar !== lastShellParts.topbar) bar.outerHTML = shell.topbar;
      if (shell.overlays !== lastShellParts.overlays) {
        captureActiveEditorFocus();
        resetDesktopViewTouchState();
        overlays.innerHTML = shell.overlays;
        applyPanelRenderEffects();
      }
      lastRenderedHtml = html;
      lastShellParts = shell;
      previousPanelSignature = panelSignature;
      syncHorizonControl(appState);
      return;
    }
  }

  previousPanelSignature = panelSignature;

  // ---- Committing to a real DOM write from here on. ----
  // If the user is typing in a known editor field, capture which one and the
  // caret offset so restoreFocusAfterRender can put them back; a remote sync
  // push mid-edit would otherwise steal focus and selection.
  captureActiveEditorFocus();
  const executionScroll = captureMobileExecutionScroll(container);
  mobileExecutionCleanup?.();
  mobileNoteSearchCleanup?.();
  const oldGantt = container.querySelector('[data-mobile-gantt-scroll]');
  if (uiState.tab === 'timeline' && oldGantt
      && Number(oldGantt.dataset.rowHeight) !== uiState.ganttRowHeight) {
    uiState.ganttRowAnchor = ganttRowAnchor(oldGantt);
  }
  // Drop any in-flight desktop-view pinch/pan whose baseline transform this
  // re-render invalidates, or the next touchmove computes against a stale
  // start transform and the visible map jumps.
  resetDesktopViewTouchState();
  // Detach the mounted desktop view and the collab note-editor host BEFORE the
  // innerHTML wipe, otherwise it destroys them. Both are re-attached by
  // applyPostRenderEffects().
  detachMountedDesktopViewToOriginalParent();
  detachMobileNoteEditor();

  mobileRoutinesController?.host.remove();
  container.innerHTML = html;
  const routinesMount=container.querySelector('[data-mobile-routines]');
  if(routinesMount) {
    hideRoutinesPanel();
    if(mobileRoutinesController)routinesMount.replaceWith(mobileRoutinesController.host);
    else mobileRoutinesController=mountRoutines(routinesMount);
  }
  lastRenderedHtml = html;
  lastShellParts = shell;
  syncVisibility(true);
  applyPostRenderEffects(appState);
  restoreMobileExecutionScroll(container, executionScroll);
  mobileExecutionCleanup = bindMobileExecutionReorder(container, { callbacks, scheduleRender, getActiveDocIdForNotes }, executionScroll);
  mobileNoteSearchCleanup = bindMobileNoteSearch(container, {callbacks, uiState, scheduleRender, ensurePendingEditsSavedBeforeNavigation, clearNoteEditDraft});
}

// If a scroll-pin was queued (e.g., before a collapse/expand mutation), restore
// the pinned node's screen position by adjusting the outline scroll.
function applyScrollPinIfNeeded() {
  if (!pendingScrollPin) return;
  const { nodeId, screenY } = pendingScrollPin;
  pendingScrollPin = null;
  if (!nodeId) return;
  const row = container?.querySelector(
    `.mobile-outline-row[data-node-id="${cssEscape(nodeId)}"]`,
  );
  if (!row) return;
  const newY = row.getBoundingClientRect().top;
  const delta = newY - screenY;
  if (Math.abs(delta) < 1) return;
  const scroller = row.closest(".mobile-view");
  if (scroller) scroller.scrollTop += delta;
}

function computePanelSignature() {
  return [
    uiState.activeNodeId || "",
    uiState.nodeEditing ? "e" : "",
    uiState.filterOpen ? "f" : "",
    uiState.searchOpen ? "s" : "",
    uiState.form?.type || "",
    uiState.confirm ? "c" : "",
    uiState.moveNodeId || "",
    uiState.moveDocId || "",
    uiState.docActionsId || "",
    uiState.folderActionsId || "",
    uiState.statusNodeId || "",
    uiState.userInfoOpen ? "u" : "",
    uiState.docPropsOpen ? "p" : "",
    uiState.notificationsOpen ? "n" : "",
    uiState.noteEditing ? "ne" : "",
  ].join("|");
}

function pinScrollToNode(nodeId) {
  if (!nodeId || !container) return;
  const row = container.querySelector(
    `.mobile-outline-row[data-node-id="${cssEscape(nodeId)}"]`,
  );
  if (!row) return;
  pendingScrollPin = { nodeId, screenY: row.getBoundingClientRect().top };
}

function applyPostRenderEffects(appState) {
  applyPanelRenderEffects();
  applyScrollPinIfNeeded();
  syncHorizonControl(appState);

  // Keep the shared spotlight dim variable in sync with the Store — the
  // desktop sidebar applies it too, but its subscriber work is skipped
  // while the mobile shell is visible.
  const dimLevel = Number.isFinite(appState?.tagDimLevel) ? appState.tagDimLevel : 80;
  document.documentElement.style.setProperty("--tag-dim-amount", String(dimLevel / 100));

  applyViewRenderEffects(appState);
}

function applyPanelRenderEffects() {
  if (container?.querySelector('[data-storage-summary]')) {
    void loadStorageSummary(container.querySelector('[data-storage-summary]'));
  }
  if (container?.querySelector('[data-storage-doc-id]')) void updateDocumentSizes(container, callbacks.VaultStorage);
  // One-shot autofocus for the add-node sheet: focus the name field with
  // the default value selected, so typing replaces "New node" directly.
  // One-shot via the flag — the shell re-renders on every store change,
  // and re-focusing/re-selecting then would fight the user mid-typing.
  if (uiState.form?.type === "add-node" && uiState.form.autofocus) {
    const nameInput = document.getElementById("mobile-add-node-name");
    if (nameInput) {
      uiState.form.autofocus = false;
      nameInput.focus();
      nameInput.select();
    }
  }
}

function applyViewRenderEffects(appState) {
  if (uiState.tab === "outline") {
    const host = container?.querySelector('.mobile-outline[data-virtualised="1"]');
    const scroller = host?.closest(".mobile-view");
    if (scroller) {
      // Restore the scroll offset the window was rendered for. A full re-render
      // replaces the scroller, which otherwise starts at 0 and would leave the
      // user at the top of the document after every edit.
      if (uiState.outlineScrollTop && scroller.scrollTop !== uiState.outlineScrollTop) {
        scroller.scrollTop = uiState.outlineScrollTop;
      }
      uiState.outlineViewportH = scroller.clientHeight;
      measureRowHeight(host);
      attachOutlineScroll();
      // The first render of a tall document happens before the viewport height
      // is known, so the initial window can be too small. Recompute once now
      // that it has been measured.
      patchOutlineWindow();
    }
  }

  if (uiState.tab === "notes") {
    const list = container.querySelector(".mobile-note-list");
    if (list) {
      // A full re-render replaces the horizontal note list, which would snap
      // it back to the newest note. Restore where the user left it — selecting
      // a note then keeps the list exactly where it was tapped.
      if (uiState.notesListScrollLeft && list.scrollLeft !== uiState.notesListScrollLeft) {
        list.scrollLeft = uiState.notesListScrollLeft;
      }
      list.addEventListener("scroll", () => {
        uiState.notesListScrollLeft = list.scrollLeft;
      }, { passive: true });
    }
  }

  if (uiState.tab === "timeline") {
    const scroller = container.querySelector("[data-mobile-gantt-scroll]");
    if (scroller) {
      attachGanttScroll(scroller, uiState, {
        today: pendingGanttScrollToToday,
        centerDay: pendingGanttCenterDay,
        scheduleRender,
        redrawRows: (s) => redrawGanttRows(s, uiState),
      });
      pendingGanttScrollToToday = false;
      pendingGanttCenterDay = null;
    }
  }

  // Mount/unmount desktop mindmap or bubble view in More preview
  syncDesktopPreviewMount(appState);

  // Mount / re-attach / tear down the mobile collab note editor based
  // on whether the user is currently editing a note in collab mode.
  // The placeholder `<div id="mobile-note-content-mount">` is emitted
  // by interfaces/classic/mobile/render/notes.js when the doc has been migrated; this
  // hook swaps in (or builds) the live TipTap+ySyncPlugin host.
  syncMobileNoteEditorMount({
    noteId: uiState.noteEditing ? (uiState.editingNoteContext?.noteId || null) : null,
    docId: uiState.noteEditing ? (uiState.editingNoteContext?.docId || null) : null,
  });
}

function syncHorizonControl(appState) {
  if (uiState.tab !== 'board' && uiState.tab !== 'timeline') return;
  renderHorizonControl(container, appState.horizon, (patch) => callbacks.Store.setHorizon(patch), appState.currentDocId);
}

function renderActiveTab(appState) {
  if (!appState.data && uiState.tab !== "docs" && uiState.tab !== "outline") {
    // No document loaded. If the vault is empty (e.g. user just deleted
    // their last doc), bounce non-outline/docs tabs into Docs with a
    // Create button so they aren't stranded on a "Loading…" message
    // that will never resolve. Outline has its own usable empty state
    // with a Create button — leave that flow alone.
    const index = callbacks.VaultStorage?.getIndex?.();
    if (!index?.docs?.length) {
      uiState.tab = "docs";
      return renderDocsTab(appState);
    }
    return renderEmpty(t("mobile.shell.loadingDocument"));
  }

  switch (uiState.tab) {
    case "docs":
      return renderDocsTab(appState);
    case "notes":
      return renderNotes(appState, {
        callbacks,
        uiState,
        getActiveDocIdForNotes,
        getNoteEditDraft,
        stashUnsavedNoteEdit,
      });
    case "routines":
      return '<div class="mobile-routines" data-mobile-routines></div>';
    case "board":
      return renderBoard(appState, {
        callbacks,
        uiState,
        collectLeafTasks,
        getBoardColumns,
        getTagDefinitions,
        nodeHasTagId,
      });
    case "timeline":
      return renderTimeline(appState, { callbacks, uiState, collectDatedEntries });
    case "mindmap":
    case "bubble":
      return renderMapView(appState, { callbacks, executionRevision }, uiState.tab);
    case "outline":
    default:
      return renderOutline(appState, outlineDeps());
  }
}

/**
 * Dependencies the outline renderer needs. Shared by the full render and the
 * scroll-time patch so the two always produce identical markup.
 */
function outlineDeps() {
  // One tag filter per outline pass (visibility + highlight rule).
  const tagFilter = callbacks.sharedCallbacks?.makeTagFilter?.();
  return {
    callbacks,
    uiState,
    getActiveDocIdForNotes,
    getInlineRenameDraft,
    getNodeTagLabels,
    getSafeStatus,
    isHiddenByTag: (node, appState) => !!tagFilter?.isHidden(node, appState.hiddenTagIds || []),
    nodeMatchesTag: (node, tagIds) => !!tagFilter?.isMatch(node, tagIds),
    renderTagChip,
  };
}

// ---------- Outline virtualisation ----------
// Scrolling must not go through the shell render: that rebuilds everything and
// would defeat the point. Instead the row window is patched straight into the
// .mobile-outline container, which is a few dozen rows rather than the whole
// document.
let outlineScrollHandler = null;
let outlineScrollRaf = 0;

function patchOutlineWindow() {
  outlineScrollRaf = 0;
  if (uiState.tab !== "outline" || !container) return;
  const host = container.querySelector('.mobile-outline[data-virtualised="1"]');
  const scroller = host?.closest(".mobile-view");
  if (!host || !scroller) return;

  uiState.outlineScrollTop = scroller.scrollTop;
  uiState.outlineViewportH = scroller.clientHeight;

  const appState = callbacks.Store.getState();
  if (!appState?.data) return;
  const deps = outlineDeps();
  const rows = flattenVisibleRows(appState, deps);
  if (!rows.length) return;

  const { first, last } = computeWindow(
    rows.length, uiState.outlineScrollTop, uiState.outlineViewportH,
  );
  // Skip the write when the same slice is already mounted — a scroll inside one
  // row's worth of travel changes nothing.
  if (first === Number(host.dataset.windowFirst) && last === Number(host.dataset.windowLast)
      && getRowHeight() === Number(host.dataset.rowHeight)) return;

  host.innerHTML = renderRowWindow(rows, appState, deps, first, last);
  host.dataset.windowFirst = String(first);
  host.dataset.windowLast = String(last);
  host.dataset.rowHeight = String(getRowHeight());
}

function attachOutlineScroll() {
  const host = container?.querySelector('.mobile-outline[data-virtualised="1"]');
  const scroller = host?.closest(".mobile-view");
  if (!scroller) return;

  // Re-attaching after every render would stack listeners on a node that
  // survives, so tag the one we own.
  if (scroller.dataset.outlineScrollBound === "1") return;
  scroller.dataset.outlineScrollBound = "1";

  outlineScrollHandler = () => {
    // rAF-throttled: scroll fires far more often than we can usefully repaint.
    if (outlineScrollRaf) return;
    outlineScrollRaf = requestAnimationFrame(patchOutlineWindow);
  };
  scroller.addEventListener("scroll", outlineScrollHandler, { passive: true });
}

function renderDocsTab(appState) {
  return renderDocs(appState, {
    callbacks,
    uiState,
    buildFolderTrail,
    docMatchesQuery,
    getActiveDocsFolderId,
    getRootFolderId,
    isOwnerDoc,
    normalizeFolderId,
  });
}

function handleClick(event) {
  handleMobileActionClick(event, getMobileActionDeps());
}

function getMobileActionDeps() {
  return {
    callbacks,
    container,
    uiState,
    isNodeLocked,
    addNodeFromForm,
    adjustGanttRowHeight,
    beginEditNode,
    beginEditNote,
    beginInlineRename,
    clearNodeEditDraft,
    clearNoteEditDraft,
    captureGanttCenterDay,
    closeTransientSheets,
    collapseAllNodes,
    collapseSelectedChildren,
    createDocFromForm,
    createFolderFromForm,
    createNote,
    ensurePendingEditsSavedBeforeNavigation,
    expandAllNodes,
    fitMountedDesktopView,
    cycleMindmapLayoutMode,
    loadDocShareInfo,
    shareInviteSubmit,
    shareSetMemberRole,
    shareCancelInvite,
    shareLinkCreate,
    shareLinkCopy,
    shareLinkSetRole,
    shareRestoreAccess,
    getCurrentDocTitle: () => getCurrentDocTitle(callbacks.Store?.getState?.() || {}),
    recoverShareFromNotification,
    dismissNotification,
    grantAccessRequest,
    getSuppressNextRowClick: () => suppressNextRowClick,
    handleBack,
    handleTitleTap,
    moveDocToFolder,
    moveNodeToParent,
    navigateUpFolder,
    openAddNodeForm,
    openAddNodeToSelectedForm,
    openDoc,
    openNode,
    openRelatedNotes,
    openRefnote,
    openSelectedProperties,
    promptInstall,
    renameDocFromForm,
    renameFolderFromForm,
    renameNodeFromForm,
    requestCancelEditNote,
    requestCloseNodePanel,
    requestDeleteDoc,
    requestDeleteFolder,
    requestDeleteNode,
    requestDeleteNote,
    runConfirmAction,
    saveNode,
    saveNote,
    scheduleRender,
    selectOutlineNode,
    setCardStatus,
    setNodeDatePreset,
    setPendingGanttScrollToToday: (value) => { pendingGanttScrollToToday = !!value; },
    setSuppressNextRowClick: (value) => { suppressNextRowClick = !!value; },
    snapshotNodeEditDraftFromDom,
    toggleCollapse,
    toggleNodeTagFromButton,
    toggleStoreIdList,
  };
}

let pendingGanttScrollToToday = true;
// Day number to recenter on the timeline after a zoom change, captured from
// the scroller pre-render so changing the zoom level doesn't snap back to today.
let pendingGanttCenterDay = null;
// Map markup otherwise stays identical when only execution/horizon changes;
// include this revision in its host so the render cache refreshes the filter.
let executionRevision = 0;
let pendingScrollPin = null; // { nodeId, screenY } — keep this row stable across re-renders
let previousPanelSignature = "";
// Markup currently mounted in `container`, so a render that would produce the
// same bytes can skip the innerHTML teardown entirely. Null means "unknown /
// container not ours", which always forces a real write.
let lastRenderedHtml = null;
// Regions the last write produced, so the next render can tell whether anything
// below the top bar actually changed. Cleared alongside lastRenderedHtml.
let lastShellParts = null;

function adjustGanttRowHeight(direction) {
  const cur = uiState.ganttRowHeight || 44;
  const factor = direction === "in" ? 1.25 : 1 / 1.25;
  const next = Math.round(cur * factor);
  uiState.ganttRowHeight = Math.max(GANTT_ROW_HEIGHT_MIN, Math.min(GANTT_ROW_HEIGHT_MAX, next));
  scheduleRender();
}

// Read the day-number currently centered in the gantt scroller using the
// PRE-zoom-change chart so a zoom-set can re-center on the same date under
// the new dayWidth instead of snapping back to today.
function captureGanttCenterDay() {
  if (!container) return;
  const scroller = container.querySelector("[data-mobile-gantt-scroll]");
  if (!scroller) return;
  pendingGanttCenterDay = ganttCenterDay(scroller);
}

function promptInstall() {
  if (!installPromptEvent) return;
  const prompt = installPromptEvent;
  installPromptEvent = null;
  prompt.prompt?.();
  Promise.resolve(prompt.userChoice).finally(() => scheduleRender());
}

/**
 * This node cannot be edited here: a map (or linked map) the user can only
 * view, or a link showing no content here (its name and children would be
 * written under the link and stay hidden). Desktop applies the same rules.
 */
function isNodeLocked(nodeId) {
  return !!(callbacks.Store.isReadOnly?.()
    || callbacks.sharedCallbacks.isNodeReadOnly?.(nodeId)
    || isInertLink(callbacks.Store.find(nodeId)));
}

function handleTitleTap(nodeId) {
  if (!nodeId) return;
  const appState = callbacks.Store.getState();
  const isSelected = appState.selectedId === nodeId;
  if (isSelected && !isNodeLocked(nodeId)) {
    beginInlineRename(nodeId);
  } else {
    selectOutlineNode(nodeId);
  }
}

function beginInlineRename(nodeId) {
  if (isNodeLocked(nodeId)) return;
  const docId = getActiveDocIdForNotes();
  if (!docId) return;
  const node = callbacks.Store.find(nodeId);
  if (!node) return;
  uiState.editingTitleNodeId = nodeId;
  uiState.inlineRenameContext = { docId, nodeId, baseline: { name: node.name || "" } };
  uiState.inlineRenameDraft = { docId, nodeId, name: node.name || "" };
  focusAfterRender = `[data-mobile-rename-input][data-node-id="${cssEscape(nodeId)}"]`;
  scheduleRender();
}

function commitInlineRename(input) {
  if (!input) return true;
  snapshotInlineRenameDraftFromDom();
  const nodeId = input.dataset.nodeId;
  const ctx = uiState.inlineRenameContext;
  const draft = getInlineRenameDraft(ctx);
  const newName = String(draft?.name ?? input.value ?? "").trim();
  if (!nodeId) {
    clearInlineRenameState();
    scheduleRender();
    return true;
  }
  // Doc-switch guard: the rename began against a specific doc; if the active
  // doc is now different (remote sync push, deep link), drop the rename
  // rather than write to the wrong document. Stash the typed title so it can be
  // recovered from the node editor if the user returns to the original doc.
  const liveDocId = getActiveDocIdForNotes();
  if (!ctx || ctx.nodeId !== nodeId || ctx.docId !== liveDocId) {
    if (ctx?.docId && ctx?.nodeId && newName && newName !== (ctx.baseline?.name || "")) {
      stashUnsavedNodeEdit(ctx.docId, ctx.nodeId, { name: newName });
      callbacks.showToast?.(
        t("mobile.toast.renameKeptDocGone"),
        { type: "warning" },
      );
    }
    clearInlineRenameState();
    scheduleRender();
    // Rename UI was cleared (either stashed or dropped). Return true so the
    // navigation guard doesn't double-warn the user about a rename that has
    // already been handled.
    return true;
  }
  const node = callbacks.Store.find(nodeId);
  if (!node || callbacks.Store.isReadOnly?.()) {
    if (!node && ctx?.docId && ctx?.nodeId && newName && newName !== (ctx.baseline?.name || "")) {
      stashUnsavedNodeEdit(ctx.docId, ctx.nodeId, { name: newName });
      callbacks.showToast?.(
        t("mobile.toast.renameKeptNodeGone"),
        { type: "warning" },
      );
    }
    clearInlineRenameState();
    scheduleRender();
    return true;
  }
  if (newName && newName !== node.name) {
    try {
      callbacks.sharedCallbacks?.onUpdateNode?.(nodeId, { name: newName });
    } catch (err) {
      showMobileError(t("mobile.error.renameNode"), err);
      scheduleRender();
      return false;
    }
  }
  clearInlineRenameState();
  scheduleRender();
  return true;
}

function cancelInlineRename() {
  clearInlineRenameState();
  scheduleRender();
}

function clearInlineRenameState() {
  uiState.editingTitleNodeId = null;
  uiState.inlineRenameContext = null;
  uiState.inlineRenameDraft = null;
}

function cssEscape(value) {
  if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(value);
  return String(value).replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

function handleKeydown(event) {
  if (event.key === 'Enter' && !event.isComposing && event.target?.id === 'mobile-add-node-name') {
    event.preventDefault();
    addNodeFromForm();
    return;
  }
  // Enter in the Today add field submits it (same as tapping "Add").
  if (event.key === "Enter" && event.target?.id === "mobile-today-add") {
    event.preventDefault();
    const btn = container?.querySelector('[data-mobile-action="today-add"]');
    if (btn) handleMobileActionClick({ target: btn, preventDefault() {} }, getMobileActionDeps());
    return;
  }
  handleMobileRenameKeydown(event, { cancelInlineRename, commitInlineRename });
}

/** Focusable descendants of `root`, in DOM order, excluding hidden ones. */
function focusableWithin(root) {
  const sel = [
    "a[href]", "button:not([disabled])", "input:not([disabled])",
    "select:not([disabled])", "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])', "[contenteditable=true]",
  ].join(",");
  return Array.from(root.querySelectorAll(sel)).filter(
    (el) => !el.hasAttribute("aria-hidden") && (el.offsetWidth || el.offsetHeight || el.getClientRects().length),
  );
}

/**
 * Keyboard semantics for the mobile sheets.
 *
 * The sheets are visually modal (full-screen panel over a backdrop) and now
 * declare `role="dialog" aria-modal="true"`, but nothing enforced that for the
 * keyboard: Tab walked straight out of the panel into the page behind it, and
 * Escape did nothing. Both are required for the dialog semantics to be honest
 * — and both matter on mobile too, where external/Bluetooth keyboards and
 * switch-control users are common.
 *
 * Bound at document level because focus can leave the mobile container.
 */
function handleDialogKeydown(event) {
  if (event.key !== "Tab" && event.key !== "Escape") return;
  if (!container) return;
  // Topmost panel == last one in DOM order (they render in a fixed sequence).
  const panels = container.querySelectorAll('.mobile-panel[role="dialog"]');
  const panel = panels[panels.length - 1];
  if (!panel) return;

  if (event.key === "Escape") {
    event.preventDefault();
    closeTransientSheets();
    scheduleRender();
    return;
  }

  const items = focusableWithin(panel);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  // Focus outside the dialog (or on the dialog itself) — pull it back in.
  if (!panel.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

function handleBlur(event) {
  handleMobileRenameBlur(event, { commitInlineRename });
}

let suppressNextRowClick = false;

// ---------- Viewport tracking (visualViewport) ----------
//
// Publishes two custom properties that the sheet CSS sizes itself from:
//
//   --mobile-keyboard-inset  how far the bottom of the layout viewport sits
//                            below what the user can actually see
//   --mobile-vh              the height of the visible area, in px
//
// --mobile-vh exists because `vh`, `dvh` and `env(safe-area-inset-*)` all turned
// out to be untrustworthy for this. `vh` on mobile is the *large* viewport (the
// height the page would have with the browser's address bar retracted), so it
// over-reports while the bar is on screen; `dvh` fixes that but needs Chrome 108
// / Safari 15.4, and older Android browsers silently fall back. A sheet sized
// from either can end up taller than the space available, and a bottom-anchored
// one then hangs off the screen.
//
// visualViewport.height is not an approximation of any of that — it is the
// measured height of what the user can see right now, including the effect of
// the address bar and the on-screen keyboard. Reading it directly removes the
// guesswork. CSS keeps `dvh`/`vh` as fallbacks for the case where this never
// runs, via the var() default.
let visualViewportHandler = null;

function setupKeyboardInsetTracking() {
  const vv = window.visualViewport;
  const root = document.documentElement;

  if (!vv) {
    // No visualViewport (very old browsers): fall back to innerHeight, which at
    // least tracks orientation changes. CSS's dvh/vh fallbacks cover the rest.
    const basic = () => root.style.setProperty("--mobile-vh", `${Math.round(window.innerHeight)}px`);
    visualViewportHandler = basic;
    window.addEventListener("resize", basic);
    window.addEventListener("orientationchange", basic);
    basic();
    return;
  }

  const update = () => {
    // Measure the gap against the LAYOUT viewport, not window.innerHeight.
    // The two engines disagree about innerHeight: Chrome reports the layout
    // viewport (constant while the address bar shows and hides), Gecko reports
    // something that tracks the visible area. On Gecko that makes
    // `innerHeight - vv.height` ≈ 0 even with the toolbar on screen, so the
    // inset comes out as zero and a bottom-anchored sheet stays pinned to the
    // bottom of the layout viewport — i.e. below what the user can see, showing
    // only its top edge. documentElement.clientHeight is the layout viewport on
    // both, so the gap is computed the same way everywhere.
    const layoutH = document.documentElement?.clientHeight || window.innerHeight;
    const inset = Math.max(0, layoutH - vv.height - vv.offsetTop);
    root.style.setProperty("--mobile-keyboard-inset", `${Math.round(inset)}px`);
    root.style.setProperty("--mobile-vh", `${Math.round(vv.height)}px`);
  };
  visualViewportHandler = update;
  vv.addEventListener("resize", update);
  vv.addEventListener("scroll", update);
  // visualViewport events don't fire for every orientation change on all
  // browsers, and a stale --mobile-vh after a rotation is exactly the state
  // that leaves a sheet hanging off the screen.
  window.addEventListener("resize", update);
  window.addEventListener("orientationchange", update);
  update();
}

function teardownKeyboardInsetTracking() {
  const vv = window.visualViewport;
  if (!visualViewportHandler) return;
  if (vv) {
    vv.removeEventListener("resize", visualViewportHandler);
    vv.removeEventListener("scroll", visualViewportHandler);
  }
  window.removeEventListener("resize", visualViewportHandler);
  window.removeEventListener("orientationchange", visualViewportHandler);
  visualViewportHandler = null;
  document.documentElement.style.removeProperty("--mobile-keyboard-inset");
  document.documentElement.style.removeProperty("--mobile-vh");
}

function handleInput(event) {
  handleMobileInput(event, {
    container,
    uiState,
    scheduleRender,
    snapshotNodeEditDraftFromDom,
    snapshotNoteEditDraftFromDom,
    commitTagDimLevel,
    // Role selects in the sharing sheet fire here (a member's, the link's):
    // without these a role chosen on a phone was never sent.
    shareSetMemberRole,
    shareLinkSetRole,
  });
}

// Fetch sharing state for the doc-properties sheet. 404 (or any failure on
// a doc that has no share) renders as "not shared"; the sheet shows a
// loading row meanwhile. Keyed by docId so a doc switch can't show stale
// members from the previous doc.
async function loadDocShareInfo() {
  const docId = callbacks.sharedCallbacks.getCurrentDocId?.();
  if (!docId || !callbacks.fetchShareDetails) {
    uiState.docShareInfo = { none: true };
    return;
  }
  // Open by its link, not as a member: what the link is, nothing to fetch.
  const held = callbacks.shareLink?.held?.(docId);
  if (held) {
    uiState.docShareInfo = { docId, held };
    scheduleRender();
    return;
  }
  uiState.docShareInfo = { loading: true, docId };
  scheduleRender();
  try {
    const share = await callbacks.fetchShareDetails(docId);
    // The map's link (docs/plans/2026-10-28-link-sharing.md); its address,
    // for the owner and admins, read after.
    const link = share && callbacks.shareLink ? await callbacks.shareLink.get(docId).catch(() => null) : null;
    if (uiState.docShareInfo?.docId !== docId) return; // doc switched meanwhile
    uiState.docShareInfo = { docId, share, link };
    if (link && ["owner", "admin"].includes(share.role)) {
      callbacks.shareLink.address(docId).then((address) => {
        if (uiState.docShareInfo?.docId !== docId || uiState.docShareInfo.link?.id !== link.id) return;
        uiState.docShareInfo.linkAddress = address;
        scheduleRender();
      }).catch(() => {});
    }
  } catch {
    if (uiState.docShareInfo?.docId !== docId) return;
    uiState.docShareInfo = { docId, none: true };
  }
  scheduleRender();
}

// ---- Share management (doc properties sheet) -------------------------------
// All actions run against the CURRENT doc, mark uiState.shareBusy while in
// flight (the sheet disables its controls), refresh the share info on
// success and surface failures via the standard error toast. The server
// enforces roles authoritatively — the sheet's gating is UX only.

async function runShareAction(label, fn) {
  if (uiState.shareBusy) return;
  const docId = callbacks.sharedCallbacks.getCurrentDocId?.();
  if (!docId) return;
  uiState.shareBusy = true;
  scheduleRender();
  try {
    await fn(docId);
    await loadDocShareInfo();
  } catch (err) {
    // A cancelled removal is not an error: its progress card says so.
    if (err?.code !== "cancelled") showMobileError(label, err);
  } finally {
    uiState.shareBusy = false;
    scheduleRender();
  }
}

function shareInviteSubmit() {
  const name = document.getElementById("mobile-share-invite-name")?.value.trim();
  const role = document.getElementById("mobile-share-invite-role")?.value || "viewer";
  if (!name) return;
  runShareAction(t("mobile.error.sendInvitation"), async (docId) => {
    await callbacks.shareInviteUser(docId, name, role);
    uiState.shareInviteDraft = null;
  });
}

function shareSetMemberRole(userId, role) {
  if (!userId || !role) return;
  runShareAction(t("mobile.error.changeRole"), (docId) =>
    callbacks.shareUpdateUserRole(docId, userId, role));
}

function shareRemoveMember(userId, linkHolder = null) {
  if (!userId) return;
  runShareAction(t("mobile.error.removeUser"), async (docId) => {
    await callbacks.shareRemoveUser(docId, userId);
    // The link is on: whoever has it opens the map, them too if they have
    // it. Offer a new one, as the computer's panel does.
    if (linkHolder !== null) {
      uiState.confirm = {
        type: "share-link-renew",
        title: t("share.link.renew"),
        message: t("share.link.stillOpenAfterRemoval", { name: linkHolder || t("mobile.confirm.thisUser") }),
      };
    }
  });
}

function shareCancelInvite(invitationId) {
  if (!invitationId) return;
  runShareAction(t("mobile.error.cancelInvitation"), (docId) =>
    callbacks.shareCancelInvitation(docId, invitationId));
}

function shareRestoreAccess(userId, username) {
  if (!userId) return;
  runShareAction(t("mobile.error.restoreAccess"), (docId) =>
    callbacks.shareRestoreMemberAccess(docId, userId, username));
}

function shareStopSharing() {
  runShareAction(t("mobile.error.stopSharing"), (docId) =>
    callbacks.shareUnshare(docId));
}

// The map's link (docs/plans/2026-10-28-link-sharing.md): the computer
// panel's actions (share-modal.js linkActions), with their progress card.
function shareLinkCreate() {
  const role = document.getElementById("mobile-share-link-role")?.value || "viewer";
  runShareAction(t("share.link.error.createFailed"), async (docId) => {
    await callbacks.shareLink.copy(await callbacks.shareLink.create(docId, role));
  });
}

function shareLinkCopy() {
  const address = uiState.docShareInfo?.linkAddress || uiState.docShareInfo?.held?.address;
  if (address) callbacks.shareLink.copy(address);
}

function shareLinkSetRole(role) {
  if (!role) return;
  runShareAction(t("share.link.error.changeFailed"), (docId) => callbacks.shareLink.setRole(docId, role));
}

function shareLinkRenew() {
  const role = uiState.docShareInfo?.link?.role || "viewer";
  runShareAction(t("share.link.error.createFailed"), async (docId) => {
    const address = await callbacks.shareLink.renew(docId, role);
    if (address) await callbacks.shareLink.copy(address);
  });
}

function shareLinkTurnOff() {
  runShareAction(t("share.link.error.changeFailed"), (docId) => callbacks.shareLink.turnOff(docId));
}

// A map kept by its link: out of this account's maps (nothing changes on
// the server); another map opens when it was the open one.
async function shareLinkRemoveHeld() {
  const docId = callbacks.sharedCallbacks.getCurrentDocId?.();
  if (!docId) return;
  uiState.docShareInfo = null;
  await callbacks.deleteDoc?.(docId);
  scheduleRender();
}

// Recover a revoked share into a local "(recovered)" copy. The heavy
// lifting (snapshot fetch, decrypt, merge, new doc) is the same
// platform-neutral flow the desktop Recover button uses.
async function recoverShareFromNotification(notificationId, shareId, shareTitle) {
  if (uiState.shareRecovering) return;
  uiState.shareRecovering = notificationId || shareId;
  scheduleRender();
  try {
    const result = await callbacks.recoverRevokedShare(notificationId, shareId, shareTitle);
    if (result?.recoveryFailed) {
      showMobileError(t("mobile.error.recoveryFailed"), new Error(t("mobile.error.recoverDocument")));
    }
  } catch (err) {
    showMobileError(t("mobile.error.recoveryFailed"), err);
  } finally {
    uiState.shareRecovering = null;
    scheduleRender();
  }
}

// Grant an access request, like the desktop "Grant access": open the map
// and its sharing section with the requester's name filled in. The owner
// picks a role and taps Invite; the server then accepts the invitation for
// the requester (invitations.rs). The notification goes once the map is open.
async function grantAccessRequest(notificationId, docId, requesterUsername) {
  if (!docId || !requesterUsername) return;
  if (callbacks.sharedCallbacks.getCurrentDocId?.() !== docId) {
    await openDoc(docId);
    // Not opened (unsaved edits kept it, or the open failed and said so).
    if (callbacks.sharedCallbacks.getCurrentDocId?.() !== docId) return;
  }
  uiState.notificationsOpen = false;
  uiState.docPropsOpen = true;
  uiState.shareInviteDraft = { name: requesterUsername, role: "viewer" };
  loadDocShareInfo();
  scheduleRender();
  if (notificationId) callbacks.markNotificationRead?.(notificationId).catch(() => {});
}

async function dismissNotification(notificationId) {
  if (!notificationId) return;
  try {
    await callbacks.markNotificationRead(notificationId);
  } catch (err) {
    showMobileError(t("mobile.error.dismissNotification"), err);
  }
  scheduleRender();
}

// Cycle the mindmap level-0 layout mode (balanced → right → left). The mode
// is a local per-doc preference read by the desktop view's render; refresh +
// re-fit is justified here — the layout change is drastic and user-initiated.
function cycleMindmapLayoutMode() {
  const order = ["balanced", "right", "left"];
  const cur = callbacks.Store.get?.("mindmapLayoutMode") || "balanced";
  const next = order[(order.indexOf(cur) + 1) % order.length];
  callbacks.Store.setMindmapLayoutMode?.(next);
  callbacks.saveToLocalStorage?.();
  refreshMountedDesktopView();
}

// Debounced Store commit for the dim slider: the CSS var previews live per
// input event; the Store write (which re-renders the sheet and would replace
// the slider mid-drag) lands only once the drag pauses.
let tagDimCommitTimer = null;
function commitTagDimLevel(level) {
  if (tagDimCommitTimer) clearTimeout(tagDimCommitTimer);
  tagDimCommitTimer = setTimeout(() => {
    tagDimCommitTimer = null;
    callbacks.Store.setTagDimLevel?.(level);
    callbacks.saveToLocalStorage?.();
  }, 300);
}

function handleBack() {
  if (uiState.notificationsOpen) {
    uiState.notificationsOpen = false;
  } else if (uiState.docPropsOpen) {
    uiState.docPropsOpen = false;
    uiState.docShareInfo = null;
  } else if (uiState.userInfoOpen) {
    uiState.userInfoOpen = false;
  } else if (uiState.filterOpen) {
    uiState.filterOpen = false;
  } else if (uiState.searchOpen) {
    uiState.searchOpen = false;
  } else if (uiState.form || uiState.confirm || uiState.moveNodeId || uiState.moveDocId || uiState.docActionsId || uiState.folderActionsId) {
    closeTransientSheets();
  } else if (uiState.activeNodeId) {
    requestCloseNodePanel();
    return;
  } else if (uiState.tab !== "outline") {
    // Tab change, treat like nav: flush in-progress edits instead of
    // silently dropping them.
    if (!ensurePendingEditsSavedBeforeNavigation()) {
      scheduleRender();
      return;
    }
    uiState.tab = "outline";
  } else if (!callbacks.Store.navigateUp?.()) {
    uiState.tab = "docs";
  }
  scheduleRender();
}

function ensurePendingEditsSavedBeforeNavigation() {
  // Inline rename: commitInlineRename now returns true whenever it cleared
  // the rename UI (saved, stashed, or dropped). Only blocks nav when the
  // bridge write threw and the rename input is still present.
  const renameInput = container?.querySelector("[data-mobile-rename-input]");
  if (renameInput) {
    commitInlineRename(renameInput);
    if (uiState.editingTitleNodeId) {
      callbacks.showToast?.(
        t("mobile.toast.finishRename"),
        { type: "warning" },
      );
      return false;
    }
  }

  snapshotActiveEditDrafts();
  if (uiState.noteEditing) {
    if (isNoteEditDirty()) {
      flushPendingNoteEditSave();
      // Block nav only if the editor is still active (write threw without
      // clearing UI). Stash-on-failure paths clear noteEditing themselves
      // and surface their own toast — proceed with nav.
      if (uiState.noteEditing) {
        callbacks.showToast?.(
          t("mobile.toast.finishNoteEdit"),
          { type: "warning" },
        );
        return false;
      }
    } else {
      // Clean exit on a not-dirty editor. For new notes (isNew=true),
      // cancelEditNote ALSO removes the optimistically-added empty note
      // from CRDT. That matches the semantic "user navigated away from
      // an empty draft = drop it" — preferable to leaving blank notes
      // littering the list.
      cancelEditNote();
    }
  }

  if (uiState.nodeEditing) {
    if (isNodeEditDirty()) {
      flushPendingNodeEditSave();
      if (uiState.nodeEditing) {
        callbacks.showToast?.(
          t("mobile.toast.finishNodeEdit"),
          { type: "warning" },
        );
        return false;
      }
    } else {
      uiState.nodeEditing = false;
      uiState.editingNodeContext = null;
      clearNodeEditDraft();
    }
  }

  return true;
}

function closeTransientSheets() {
  uiState.searchOpen = false;
  uiState.form = null; uiState.formDraft = null;
  uiState.confirm = null;
  uiState.moveNodeId = null;
  uiState.moveDocId = null;
  uiState.docActionsId = null;
  uiState.folderActionsId = null;
  uiState.statusNodeId = null;
  uiState.statusSourceTagId = null;
  uiState.userInfoOpen = false;
  uiState.notificationsOpen = false;
  uiState.docPropsOpen = false;
  uiState.docShareInfo = null;
}

function resetTransientUi() {
  // If a recovery confirm was accepted but a doc-switch race fired before
  // the render consumed the payload, re-stash so the take didn't permanently
  // lose the user's recovered text.
  const r = uiState.recoveredEditPayload;
  if (r) {
    if (r.kind === "note" && r.docId && r.noteId) {
      stashUnsavedNoteEdit(r.docId, r.noteId, {
        title: r.title, content: r.content, updated: r.updated,
      });
    } else if (r.kind === "node" && r.docId && r.nodeId) {
      stashUnsavedNodeEdit(r.docId, r.nodeId, {
        name: r.name, startDate: r.startDate, endDate: r.endDate, notes: r.notes, color: r.color,
      });
    }
  }
  uiState.activeNodeId = null;
  uiState.nodeEditing = false;
  uiState.activeNoteId = null;
  uiState.activeNoteDocId = null;
  uiState.noteEditing = false;
  uiState.editingNoteContext = null;
  uiState.editingNodeContext = null;
  uiState.inlineRenameContext = null;
  uiState.noteEditDraft = null;
  uiState.nodeEditDraft = null;
  uiState.inlineRenameDraft = null;
  uiState.editingTitleNodeId = null;
  uiState.recoveredEditPayload = null;
  uiState.notesListScrollLeft = 0;
  uiState.todayDrafts.clear();
  uiState.notesSearchQuery = "";
  uiState.noteSearchMatch = null;
  uiState.noteSearchReveal = false;
  closeTransientSheets();
}

function snapshotActiveEditDrafts() {
  snapshotNodeEditDraftFromDom();
  snapshotNoteEditDraftFromDom();
  snapshotInlineRenameDraftFromDom();
  const input = container?.querySelector('#mobile-today-add');
  if (input?.dataset.noteId) {
    if (input.value) uiState.todayDrafts.set(input.dataset.noteId, input.value);
    else uiState.todayDrafts.delete(input.dataset.noteId);
  }
}

function snapshotNodeEditDraftFromDom() {
  const ctx = uiState.editingNodeContext;
  if (!uiState.nodeEditing || !ctx) return;
  const nameEl = document.getElementById("mobile-node-name");
  const startEl = document.getElementById("mobile-node-start");
  const endEl = document.getElementById("mobile-node-end");
  const notesEl = document.getElementById("mobile-node-notes");
  if (!nameEl && !startEl && !endEl && !notesEl) return;
  const previous = getNodeEditDraft(ctx) || {};
  uiState.nodeEditDraft = {
    docId: ctx.docId,
    nodeId: ctx.nodeId,
    name: nameEl ? nameEl.value : previous.name,
    startDate: startEl ? startEl.value : previous.startDate,
    endDate: endEl ? endEl.value : previous.endDate,
    notes: notesEl ? notesEl.value : previous.notes,
    // Color isn't a DOM input — it's swatch-driven and lives only in the
    // draft. Carry the previous draft value through so DOM-only snapshot
    // calls don't drop the in-flight color selection.
    color: previous.color,
  };
}

function snapshotNoteEditDraftFromDom() {
  const ctx = uiState.editingNoteContext;
  if (!uiState.noteEditing || !ctx) return;
  const titleEl = document.getElementById("mobile-note-title");
  const contentEl = document.getElementById("mobile-note-content");
  // In collab mode the textarea doesn't exist (the editor is TipTap+
  // ySyncPlugin on a mount div) and the content lives in the
  // Y.XmlFragment. We must NOT snapshot a content value here, because
  // any later save path that reads draft.content would write that
  // value into the fragment via setDocNoteFromMarkdown, clobbering
  // peer edits. Title still snapshots normally.
  const collab = !!callbacks.sharedCallbacks?.isCollabDocNotesEnabled?.(ctx.docId);
  if (!titleEl && !contentEl && !collab) return;
  const previous = getNoteEditDraft(ctx) || {};
  uiState.noteEditDraft = collab
    ? {
        docId: ctx.docId,
        noteId: ctx.noteId,
        title: titleEl ? titleEl.value : previous.title,
        // No content — fragment is the source of truth.
      }
    : {
        docId: ctx.docId,
        noteId: ctx.noteId,
        title: titleEl ? titleEl.value : previous.title,
        content: contentEl ? contentEl.value : previous.content,
      };
}

function snapshotInlineRenameDraftFromDom() {
  const ctx = uiState.inlineRenameContext;
  if (!ctx) return;
  // Defensive: the selector matches any rename input. Only snapshot if its
  // data-node-id matches the active context, so a stale input from a
  // partial render can't poison the draft for a different node.
  const input = container?.querySelector(
    `[data-mobile-rename-input][data-node-id="${cssEscape(ctx.nodeId)}"]`,
  );
  if (!input) return;
  uiState.inlineRenameDraft = {
    docId: ctx.docId,
    nodeId: ctx.nodeId,
    name: input.value || "",
  };
}

function getNodeEditDraft(ctx = uiState.editingNodeContext) {
  const draft = uiState.nodeEditDraft;
  if (!ctx || !draft) return null;
  return draft.docId === ctx.docId && draft.nodeId === ctx.nodeId ? draft : null;
}

function getNoteEditDraft(ctx = uiState.editingNoteContext) {
  const draft = uiState.noteEditDraft;
  if (!ctx || !draft) return null;
  return draft.docId === ctx.docId && draft.noteId === ctx.noteId ? draft : null;
}

function getInlineRenameDraft(ctx = uiState.inlineRenameContext) {
  const draft = uiState.inlineRenameDraft;
  if (!ctx || !draft) return null;
  return draft.docId === ctx.docId && draft.nodeId === ctx.nodeId ? draft : null;
}

function clearNodeEditDraft() {
  uiState.nodeEditDraft = null;
}

function clearNoteEditDraft() {
  uiState.noteEditDraft = null;
}

function stashCurrentNodeEditIfDirty(ctx = uiState.editingNodeContext) {
  if (!ctx) return false;
  snapshotNodeEditDraftFromDom();
  if (!isNodeEditDirty(ctx)) return false;
  const draft = getNodeEditDraft(ctx);
  if (!draft) return false;
  stashUnsavedNodeEdit(ctx.docId, ctx.nodeId, {
    name: draft.name ?? ctx.baseline?.name ?? "",
    startDate: draft.startDate ?? ctx.baseline?.startDate ?? "",
    endDate: draft.endDate ?? ctx.baseline?.endDate ?? "",
    notes: draft.notes ?? ctx.baseline?.notes ?? "",
    color: draft.color ?? ctx.baseline?.color ?? "",
  });
  return true;
}

function stashCurrentNoteEditIfDirty(ctx = uiState.editingNoteContext) {
  if (!ctx) return false;
  snapshotNoteEditDraftFromDom();
  if (!isNoteEditDirty(ctx)) return false;
  const draft = getNoteEditDraft(ctx);
  if (!draft) return false;
  stashUnsavedNoteEdit(ctx.docId, ctx.noteId, {
    title: String(draft.title ?? ctx.baseline?.title ?? ""),
    content: String(draft.content ?? ctx.baseline?.content ?? ""),
    updated: new Date().toISOString(),
  });
  return true;
}

// Dirty checks compare the draft against the BASELINE captured at edit-open,
// not the live node/note. A remote rename mid-edit would otherwise produce
// a spurious "Discard changes?" prompt even when the user typed nothing.
function isNodeEditDirty(ctx = uiState.editingNodeContext) {
  const draft = getNodeEditDraft(ctx);
  if (!ctx || !draft) return false;
  const base = ctx.baseline || {};
  return (
    (draft.name ?? "") !== (base.name ?? "") ||
    (draft.startDate || "") !== (base.startDate || "") ||
    (draft.endDate || "") !== (base.endDate || "") ||
    (draft.notes ?? "") !== (base.notes ?? "") ||
    (draft.color ?? "") !== (base.color ?? "")
  );
}

function isNoteEditDirty(ctx = uiState.editingNoteContext) {
  const draft = getNoteEditDraft(ctx);
  if (!ctx || !draft) return false;
  const base = ctx.baseline || {};
  // In collab mode the content lives in the Y.XmlFragment, not the
  // draft/baseline. Every keystroke is already a CRDT op — there's
  // nothing to "discard" content-wise, and comparing here is unsafe:
  // the draft has no content field (snapshotNoteEditDraftFromDom
  // intentionally omits it), and baseline.content may still hold a
  // pre-Day-5 stale string. Only the title can be dirty.
  const collab = !!callbacks.sharedCallbacks?.isCollabDocNotesEnabled?.(ctx.docId);
  const titleChanged = (draft.title ?? "") !== (base.title ?? "");
  if (collab) return titleChanged;
  return titleChanged || (draft.content ?? "") !== (base.content ?? "");
}

function sanitizeUiState(appState) {
  if (uiState.activeNodeId && !callbacks.Store.find(uiState.activeNodeId)) {
    stashCurrentNodeEditIfDirty();
    uiState.activeNodeId = null;
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
    clearNodeEditDraft();
  }
  // If the note being edited got remote-deleted, the edit panel would
  // otherwise render against the wrong (fallback) note and a Save would
  // misroute. Stash the in-flight content for recovery and close the editor.
  if (uiState.noteEditing && uiState.editingNoteContext) {
    const ctx = uiState.editingNoteContext;
    const docId = ctx.docId;
    const notes = callbacks.sharedCallbacks.getDocNotes?.(docId) || [];
    if (!notes.some((n) => n.id === ctx.noteId)) {
      stashCurrentNoteEditIfDirty(ctx);
      callbacks.showToast?.(
        t("mobile.toast.noteEditClosed"),
        { type: "warning" },
      );
      uiState.noteEditing = false;
      uiState.editingNoteContext = null;
      clearNoteEditDraft();
    }
  }
  if (callbacks.Store.isReadOnly?.()) {
    stashCurrentNoteEditIfDirty();
    stashCurrentNodeEditIfDirty();
    uiState.noteEditing = false;
    uiState.nodeEditing = false;
    uiState.editingNoteContext = null;
    uiState.editingNodeContext = null;
    clearNoteEditDraft();
    clearNodeEditDraft();
  }
  if (uiState.moveNodeId && !callbacks.Store.find(uiState.moveNodeId)) {
    uiState.moveNodeId = null;
  }
  if (uiState.statusNodeId && !callbacks.Store.find(uiState.statusNodeId)) {
    uiState.statusNodeId = null;
  }
  const index = callbacks.VaultStorage?.getIndex?.();
  if (uiState.docActionsId && !findDoc(uiState.docActionsId)) {
    uiState.docActionsId = null;
  }
  if (uiState.moveDocId && !findDoc(uiState.moveDocId)) {
    uiState.moveDocId = null;
  }
  if (uiState.folderActionsId && !findFolder(uiState.folderActionsId)) {
    uiState.folderActionsId = null;
  }
  if (uiState.docsFolderId && !(index?.folders || []).some((folder) => folder.id === uiState.docsFolderId)) {
    uiState.docsFolderId = getRootFolderId(index);
  }
  if (!appState.data && uiState.tab !== "docs") {
    stashCurrentNodeEditIfDirty();
    uiState.activeNodeId = null;
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
    clearNodeEditDraft();
    uiState.moveNodeId = null;
  }
}

function openNode(nodeId) {
  if (!nodeId) return;
  callbacks.Store.setSelected(nodeId);
  uiState.activeNodeId = nodeId;
  uiState.nodeEditing = false;
  uiState.editingNodeContext = null;
  clearNodeEditDraft();
  scheduleRender();
}

async function openRefnote(nodeId, linkedDocId) {
  if (!nodeId) return;
  const visibleNode = callbacks.Store.find(nodeId);
  if (visibleNode) {
    if (!ensurePendingEditsSavedBeforeNavigation()) return;
    uiState.tab = "outline";
    openNode(nodeId);
    return;
  }

  if (linkedDocId) {
    // Validate the linked doc id against the local vault index. A refnote
    // payload from a shared note can in principle carry an arbitrary doc id;
    // restricting to docs the user already has access to means we never
    // hand an unknown id to the loader.
    if (!findDoc(linkedDocId)) {
      callbacks.showToast?.(t("mobile.toast.refDocUnavailable"), { type: "warning" });
      scheduleRender();
      return;
    }
    try {
      if (!ensurePendingEditsSavedBeforeNavigation()) return;
      await callbacks.openDoc?.(linkedDocId);
      uiState.tab = "outline";
      if (callbacks.Store.find(nodeId)) {
        openNode(nodeId);
      } else {
        callbacks.showToast?.(t("mobile.toast.refNodeNotInDoc"), { type: "warning" });
        scheduleRender();
      }
    } catch (err) {
      showMobileError(t("mobile.error.openRefDoc"), err);
    }
    return;
  }

  uiState.tab = "outline";
  callbacks.showToast?.(t("mobile.toast.refNodeUnavailable"), { type: "warning" });
  scheduleRender();
}

function openSelectedProperties() {
  const state = callbacks.Store.getState();
  const nodeId = state.selectedId || state.focusId || state.data?.id;
  if (nodeId) openNode(nodeId);
}

function requestCloseNodePanel() {
  if (uiState.nodeEditing) {
    snapshotNodeEditDraftFromDom();
    if (isNodeEditDirty()) {
      uiState.confirm = {
        type: "discard-node-edit",
        title: t("mobile.confirm.discardTitle"),
        message: t("mobile.confirm.discardNode"),
      };
      scheduleRender();
      return;
    }
  }
  closeNodePanelDiscardingDraft();
}

function closeNodePanelDiscardingDraft() {
  uiState.activeNodeId = null;
  uiState.nodeEditing = false;
  uiState.editingNodeContext = null;
  clearNodeEditDraft();
  scheduleRender();
}

function requestCancelEditNote() {
  if (uiState.noteEditing) {
    snapshotNoteEditDraftFromDom();
    const ctx = uiState.editingNoteContext;
    const collab = !!callbacks.sharedCallbacks?.isCollabDocNotesEnabled?.(ctx?.docId);
    // Collab mode: content edits are already CRDT-persisted in the
    // Y.XmlFragment — "Discard" can't undo them, so a discard prompt
    // would be misleading. Silently save the title (if it changed)
    // and close, matching the user expectation that pressing X here is
    // "I'm done editing, keep what I typed." This also resolves the
    // residual UX mismatch where the close icon labelled "Close"
    // looked like a destructive Cancel.
    if (collab) {
      if (ctx?.noteId && isNoteEditDirty(ctx)) {
        saveNote(ctx.noteId);
        return;
      }
      cancelEditNote();
      return;
    }
    if (isNoteEditDirty()) {
      uiState.confirm = {
        type: "discard-note-edit",
        title: t("mobile.confirm.discardTitle"),
        message: t("mobile.confirm.discardNote"),
      };
      scheduleRender();
      return;
    }
  }
  cancelEditNote();
}

function selectOutlineNode(nodeId) {
  if (!nodeId) return;
  // Pin the tapped row's screen position before re-render. innerHTML wipe
  // resets scrollTop to 0, so without this every selection would scroll the
  // outline back to the top.
  pinScrollToNode(nodeId);
  callbacks.Store.setSelected(nodeId);
  scheduleRender();
}

// Snapshot the doc/node ids the editor was opened against. Compared at save
// time so a remote doc-switch (sync push, refnote nav) can't sneak edits onto
// the wrong node.
function beginEditNode() {
  const nodeId = uiState.activeNodeId;
  const docId = getActiveDocIdForNotes();
  if (!nodeId || !docId) return;
  const node = callbacks.Store.find(nodeId);
  // Snapshot the values at edit-open as the dirty-check baseline. Comparing
  // the draft to a remote-updated live node would falsely mark as dirty
  // when sync renames the node mid-edit, leading to a spurious discard
  // confirm even though the user typed nothing.
  const baseline = {
    name: node?.name || "",
    startDate: node?.startDate || "",
    endDate: node?.endDate || "",
    notes: node?.notes || "",
    color: node?.color || "",
  };
  uiState.editingNodeContext = { docId, nodeId, baseline };
  uiState.nodeEditDraft = {
    docId,
    nodeId,
    ...baseline,
  };
  uiState.nodeEditing = true;
  scheduleRender();
  const stashed = peekStashedNodeEdit(docId, nodeId);
  if (stashed && (stashed.name || stashed.notes || stashed.startDate || stashed.endDate || stashed.color)) {
    uiState.confirm = {
      type: "recover-node",
      docId,
      nodeId,
      title: t("mobile.confirm.recoverNodeTitle"),
      message: t("mobile.confirm.recoverMessage"),
    };
  }
}

function saveNode() {
  if (callbacks.Store.isReadOnly?.()) return;
  snapshotNodeEditDraftFromDom();
  const ctx = uiState.editingNodeContext;
  const nodeId = ctx?.nodeId || uiState.activeNodeId;
  if (!nodeId) return;
  const liveDocId = getActiveDocIdForNotes();
  if (!ctx || ctx.nodeId !== nodeId || ctx.docId !== liveDocId) {
    const stashed = stashCurrentNodeEditIfDirty(ctx);
    callbacks.showToast?.(
      stashed
        ? t("mobile.toast.nodeSaveKept")
        : t("mobile.toast.nodeSaveCancelled"),
      { type: "warning" },
    );
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
    clearNodeEditDraft();
    scheduleRender();
    return;
  }
  if (!callbacks.Store.find(nodeId)) {
    stashCurrentNodeEditIfDirty(ctx);
    callbacks.showToast?.(
      t("mobile.toast.nodeEditKeptNodeGone"),
      { type: "warning" },
    );
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
    clearNodeEditDraft();
    scheduleRender();
    return;
  }

  const draft = getNodeEditDraft(ctx) || {};
  const name = String(draft.name ?? document.getElementById("mobile-node-name")?.value ?? "").trim() || t("common.untitled");
  const startDate = draft.startDate || document.getElementById("mobile-node-start")?.value || null;
  const endDate = draft.endDate || document.getElementById("mobile-node-end")?.value || null;
  const notes = String(draft.notes ?? document.getElementById("mobile-node-notes")?.value ?? "");
  const baselineColor = ctx.baseline?.color || "";
  const draftColor = draft.color != null ? draft.color : baselineColor;
  const colorChanged = !!draftColor && draftColor !== baselineColor;
  const updates = { name, startDate, endDate, notes };
  if (colorChanged) updates.color = draftColor;

  try {
    if (colorChanged) {
      // Color change cascades to all descendants like the desktop modal.
      // Wrap node + descendant writes in a single CRDT batch so subscribers
      // see one coherent transaction instead of N separate updates.
      callbacks.sharedCallbacks.beginBatch?.();
      try {
        callbacks.sharedCallbacks.onUpdateNode?.(nodeId, updates);
        const liveNode = callbacks.Store.find(nodeId);
        if (liveNode) cascadeNodeColor(liveNode, draftColor);
      } finally {
        callbacks.sharedCallbacks.endBatch?.();
      }
    } else {
      callbacks.sharedCallbacks.onUpdateNode?.(nodeId, updates);
    }
  } catch (err) {
    // Bridge write threw (storage health, etc.). Stash the draft + close
    // editor so the user can recover instead of being stuck with a save
    // button that never works while StorageHealth is broken.
    showMobileError(t("mobile.error.saveNodeKept"), err);
    stashUnsavedNodeEdit(ctx.docId, ctx.nodeId, {
      name, startDate, endDate, notes, color: draftColor,
    });
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
    clearNodeEditDraft();
    scheduleRender();
    return;
  }
  uiState.nodeEditing = false;
  uiState.editingNodeContext = null;
  clearNodeEditDraft();
  callbacks.saveToLocalStorage?.();
  scheduleRender();
}

// Walk every descendant of `node` and write the new color. Mirrors the
// desktop modal-manager cascade so the mobile color picker matches the
// "color applies to children" expectation. Caller is responsible for the
// surrounding beginBatch/endBatch.
function cascadeNodeColor(node, color) {
  const children = node?.children || [];
  for (const child of children) {
    callbacks.sharedCallbacks.onUpdateNode?.(child.id, { color });
    cascadeNodeColor(child, color);
  }
}

function setNodeDatePreset(preset) {
  const startInput = document.getElementById("mobile-node-start");
  const endInput = document.getElementById("mobile-node-end");
  if (!startInput || !endInput) return;

  if (preset === "clear") {
    startInput.value = "";
    endInput.value = "";
    snapshotNodeEditDraftFromDom();
    return;
  }

  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (preset === "tomorrow") {
    start.setDate(start.getDate() + 1);
  }
  const end = new Date(start);
  if (preset === "week") {
    end.setDate(end.getDate() + 7);
  }

  startInput.value = toDateInputValue(start);
  endInput.value = toDateInputValue(end);
  snapshotNodeEditDraftFromDom();
}

function toggleCollapse(nodeId) {
  // Read-only viewers cannot persist `collapsed` to CRDT — desktop callback
  // would also reject, but we surface earlier and avoid a noisy error path.
  if (callbacks.Store.isReadOnly?.()) return;
  const node = callbacks.Store.find(nodeId);
  if (!node) return;
  pinScrollToNode(nodeId);
  try {
    callbacks.sharedCallbacks.onUpdateNode?.(nodeId, { collapsed: !node.collapsed });
  } catch (err) {
    showMobileError(t("mobile.error.toggleCollapse"), err);
  }
}

function openAddNodeForm(parentId) {
  if (callbacks.Store.isReadOnly?.()) return;
  if (!parentId) return;
  if (isNodeLocked(parentId)) {
    // Say why, as the data layer would after the form.
    const inert = isInertLink(callbacks.Store.find(parentId));
    callbacks.showToast?.(t(inert ? "sync.edit.linkedNotShown" : "sync.edit.linkedUnavailable"), { type: "warning" });
    return;
  }
  // autofocus: consumed once by applyPostRenderEffects after the sheet
  // renders — focuses the name field with the default text selected.
  uiState.formDraft = null;
  uiState.form = {
    type: 'add-node', parentId, autofocus: true,
    docId: callbacks.Store.get('currentDocId'),
    tagIds: inProgressTags(getTagDefinitions(parentId)).map(tag => tag.tagId),
    estimate: null,
  };
  scheduleRender();
}

function openAddNodeAtRoot() {
  if (callbacks.Store.isReadOnly?.()) return;
  const rootId = callbacks.Store.getState()?.data?.id;
  if (!rootId) return;
  openAddNodeForm(rootId);
}

function addChild(parentId, name, details = {}) {
  if (callbacks.Store.isReadOnly?.()) return;
  if (!parentId) return;
  const parent = callbacks.Store.find(parentId);
  if (!parent) return;
  if (!name) return;

  const state = callbacks.Store.getState();
  const rootId = state.data?.id;
  const childColor = parentId === rootId
    ? callbacks.sharedCallbacks.getNewColor?.((parent.children || []).map((child) => child.color)) || parent.color
    : parent.color;
  const childId = callbacks.sharedCallbacks.generateId?.();
  if (!childId) {
    showMobileError(t("mobile.error.addNode"), new Error("generateId unavailable"));
    return;
  }

  try {
    const added = callbacks.sharedCallbacks.onAddNode?.(parentId, {
      id: childId,
      name,
      color: childColor || "#6b7280",
      ...details,
      children: [],
    });
    if (!added) {
      callbacks.showToast?.(t("mobile.toast.nodeNotAdded"), { type: "warning" });
      return;
    }
  } catch (err) {
    showMobileError(t("mobile.error.addNode"), err);
    return;
  }
  callbacks.Store.setSelected?.(childId);
  callbacks.saveToLocalStorage?.();
  return true;
}

async function openDoc(docId) {
  return docActions.openDoc(docId);
}

async function createDocFromForm() {
  return docActions.createDocFromForm();
}

async function createFolderFromForm() {
  return docActions.createFolderFromForm();
}

async function renameDocFromForm() {
  return docActions.renameDocFromForm();
}

async function renameFolderFromForm() {
  return docActions.renameFolderFromForm();
}

function openAddNodeToSelectedForm() {
  const state = callbacks.Store.getState();
  const selected = state.selectedId && callbacks.Store.find(state.selectedId);
  const parentId = selected?.id || state.focusId || state.data?.id;
  openAddNodeForm(parentId);
}

function addNodeFromForm() {
  const form = uiState.form;
  if (!form || form.type !== "add-node") return;
  if (form.docId !== callbacks.Store.get('currentDocId')) {
    callbacks.showToast?.(t("mobile.toast.returnToAdd"), {type: 'warning'});
    return;
  }
  if (callbacks.Store.isReadOnly?.()) {
    callbacks.showToast?.(t("app.nodeModal.notEditable"), {type: 'warning'});
    return;
  }
  const name = document.getElementById("mobile-add-node-name")?.value.trim() || t("mobile.form.newNode");
  const tags = form.tagIds.map(tagId => ({tagId, setAt: new Date().toISOString()}));
  if (!addChild(form.parentId, name, {tags, estimate: normalizeEstimate(form.estimate)})) return;
  uiState.form = null; uiState.formDraft = null;
  scheduleRender();
}

function renameNodeFromForm() {
  if (callbacks.Store.isReadOnly?.()) return;
  const nodeId = uiState.form?.nodeId;
  const name = document.getElementById("mobile-rename-node-name")?.value.trim();
  if (!nodeId || !name) return;
  if (!callbacks.Store.find(nodeId)) {
    callbacks.showToast?.(t("mobile.toast.nodeUnavailable"), { type: "warning" });
    uiState.form = null; uiState.formDraft = null;
    scheduleRender();
    return;
  }
  try {
    callbacks.sharedCallbacks.onUpdateNode?.(nodeId, { name });
  } catch (err) {
    showMobileError(t("mobile.error.renameNode"), err);
    return;
  }
  uiState.form = null; uiState.formDraft = null;
  callbacks.saveToLocalStorage?.();
  scheduleRender();
}

function createNote() {
  if (callbacks.Store.isReadOnly?.()) return;
  const docId = getActiveDocIdForNotes();
  if (!docId) {
    showMobileError(t("mobile.error.cannotCreateNote"), new Error("no active document"));
    return;
  }
  const now = new Date().toISOString();
  const note = {
    id: callbacks.sharedCallbacks.generateId?.() || `note-${Date.now()}`,
    title: "",
    content: "",
    created: now,
    updated: now,
    refnotes: [],
  };
  try {
    callbacks.sharedCallbacks.addDocNote?.(note, docId);
  } catch (err) {
    showMobileError(t("mobile.error.createNote"), err);
    return;
  }
  // CRDTBridge.addDocNote silently no-ops when no docManager resolves for
  // docId (logged-out + no current doc, or shared-doc edge cases). Verify
  // the note actually landed before opening the editor — otherwise the
  // user types into a phantom note that can never be saved.
  const after = callbacks.sharedCallbacks.getDocNotes?.(docId) || [];
  if (!after.some((n) => n.id === note.id)) {
    showMobileError(
      t("mobile.error.createNote"),
      new Error("note manager unavailable for this document"),
    );
    return;
  }
  // A new note is a new period: pinned items carry over automatically
  // (design spec §3.4); everything else waits for an explicit carry-over.
  try {
    const sc = callbacks.sharedCallbacks;
    if (sc.isExecutionEnabled?.() && sc.getCarryCandidates) {
      // Sends prepend; preserve the previous note’s order among pinned tasks.
      for (const c of [...sc.getCarryCandidates(note.id)].reverse()) {
        if (c.pinned) sc.sendToExecution(note.id, c.nodeId, { pinned: true, keepPostponement: true });
      }
    }
  } catch (err) {
    console.warn("[Mobile] auto-carry of pinned items failed:", err);
  }
  uiState.activeNoteId = note.id;
  uiState.activeNoteDocId = null;
  setTaskFilter(null);
  uiState.notesSearchQuery = "";
  uiState.noteEditing = true;
  // A fresh note sorts to the front of the list — snap the list back so it's
  // visible (the scroll restore would otherwise keep it off-screen).
  uiState.notesListScrollLeft = 0;
  // New note baseline: empty title/content. isNoteEditDirty becomes true as
  // soon as the user types anything, which is correct.
  uiState.editingNoteContext = {
    docId,
    noteId: note.id,
    baseline: { title: "", content: "" },
    isNew: true,
  };
  uiState.noteEditDraft = { docId, noteId: note.id, title: "", content: "" };
  scheduleRender();
}

// Capture the doc/note ids at edit-open so a later save can detect a
// doc-switch race and refuse to write the wrong note in the wrong doc.
function beginEditNote() {
  if (uiState.activeNoteDocId) return;
  const noteId = uiState.activeNoteId;
  const docId = getActiveDocIdForNotes();
  if (!noteId || !docId) return;
  const note = (callbacks.sharedCallbacks.getDocNotes?.(docId) || callbacks.sharedCallbacks.getDocNotes?.() || [])
    .find((item) => item.id === noteId);
  // Same baseline pattern as beginEditNode — see comment there.
  const baseline = {
    title: note?.title || "",
    content: note?.content || "",
  };
  uiState.editingNoteContext = { docId, noteId, baseline };
  uiState.noteEditDraft = {
    docId,
    noteId,
    ...baseline,
  };
  uiState.noteEditing = true;
  scheduleRender();
  // If a previous edit was stashed because the doc was switched mid-typing,
  // offer to recover it. Peek (don't take) so a Cancel doesn't lose the
  // stash; runConfirmAction takes it on accept.
  const stashed = peekStashedNoteEdit(docId, noteId);
  if (stashed && (stashed.title || stashed.content)) {
    uiState.confirm = {
      type: "recover-note",
      docId,
      noteId,
      title: t("mobile.confirm.recoverNoteTitle"),
      message: t("mobile.confirm.recoverMessage"),
    };
  }
}

function cancelEditNote() {
  const ctx = uiState.editingNoteContext;
  uiState.noteEditing = false;
  uiState.editingNoteContext = null;
  clearNoteEditDraft();
  // New notes are created optimistically so the editor has a real CRDT target.
  // If the user closes/discards before saving, remove that untouched draft note
  // instead of leaving a blank document note behind.
  //
  // Collab mode caveat: in yxml-v1 mode the user might have typed body
  // content (CRDT-ops into the fragment) without touching the title.
  // isNoteEditDirty there only sees the title, so this cancel path would
  // otherwise delete a note with real user typing in it. Probe the
  // fragment directly and skip the remove when the body has content.
  if (ctx?.isNew && ctx.docId && ctx.noteId && !callbacks.Store.isReadOnly?.()) {
    const sc = callbacks.sharedCallbacks;
    const collab = !!sc?.isCollabDocNotesEnabled?.(ctx.docId);
    // In collab mode, default to ASSUMING content exists when the
    // probe can't run cleanly. Erring toward "keep the note" on any
    // serializer hiccup is the data-safe choice — at worst the user
    // sees an empty note they can delete manually. The previous
    // default (treat probe error as empty → remove) could destroy
    // typed content if serializeDocNote ever threw on the cancel path.
    // Default to "has content" in collab mode so we err toward
    // keeping the note whenever we can't conclusively prove the
    // fragment is empty. Three cases now distinguished:
    //   (a) probe callback missing — keep note (couldn't check)
    //   (b) probe threw          — keep note (can't trust result)
    //   (c) probe returned a string — use its trim length
    // Previously (a) and (c-with-undefined-return) both produced
    // hasFragmentContent=false and silently deleted notes when the
    // callback wasn't wired.
    let hasFragmentContent = collab;
    if (collab) {
      if (typeof sc?.serializeDocNote === "function") {
        try {
          const md = sc.serializeDocNote(ctx.noteId, ctx.docId);
          hasFragmentContent = typeof md === "string" && md.trim().length > 0;
        } catch (err) {
          console.warn("[Mobile] serializeDocNote threw on cancel — keeping note as a precaution:", err);
          hasFragmentContent = true;
        }
      }
    }
    if (!hasFragmentContent) {
      try {
        sc?.removeDocNote?.(ctx.noteId, ctx.docId);
      } catch (err) {
        showMobileError(t("mobile.error.removeEmptyNote"), err);
      }
      if (uiState.activeNoteId === ctx.noteId) uiState.activeNoteId = null;
    }
  }
  scheduleRender();
}

function saveNote(noteId) {
  if (callbacks.Store.isReadOnly?.()) return;
  snapshotNoteEditDraftFromDom();
  const ctx = uiState.editingNoteContext;
  const liveDocId = getActiveDocIdForNotes();
  // Refuse to write if the editor was opened against a different doc/note than
  // the one we'd write to now. This is the doc-switch / note-switch guard.
  if (!ctx || ctx.noteId !== noteId || ctx.docId !== liveDocId) {
    const stashed = stashCurrentNoteEditIfDirty(ctx);
    callbacks.showToast?.(
      stashed
        ? t("mobile.toast.noteSaveKept")
        : t("mobile.toast.noteSaveCancelled"),
      { type: "warning" },
    );
    uiState.noteEditing = false;
    uiState.editingNoteContext = null;
    clearNoteEditDraft();
    scheduleRender();
    return;
  }
  const draft = getNoteEditDraft(ctx) || {};
  const title = String(draft.title ?? document.getElementById("mobile-note-title")?.value ?? "");
  const updated = new Date().toISOString();
  // Collab mode: content lives in the Y.XmlFragment, has been written
  // there CRDT-op-at-a-time by ySyncPlugin as the user typed, so we
  // MUST NOT write `content` here — that would clear-and-rebuild the
  // fragment and clobber any concurrent edits from a desktop tab. The
  // textarea is only present in non-collab mode, so its value is only
  // read when we're falling back to the legacy path.
  const collab = !!callbacks.sharedCallbacks.isCollabDocNotesEnabled?.(ctx.docId);
  const writeContent = !collab;
  const content = writeContent
    ? String(draft.content ?? document.getElementById("mobile-note-content")?.value ?? "")
    : null;
  let saved = false;
  try {
    const updates = noteEditUpdates(ctx.baseline, { title, content }, { collab, updated });
    saved = !!callbacks.sharedCallbacks.updateDocNote?.(
      noteId,
      updates,
      ctx.docId,
    );
  } catch (err) {
    // Bridge write threw (storage health, etc.). Stash + close editor so
    // the user can recover instead of being stuck.
    showMobileError(t("mobile.error.saveNoteKept"), err);
    // In collab mode the content is already CRDT-synced into the
    // fragment, so we only need to stash the title (otherwise we'd
    // store a null content and confuse recovery). In legacy mode we
    // stash everything as before.
    stashUnsavedNoteEdit(ctx.docId, ctx.noteId, writeContent
      ? { title, content, updated }
      : { title, updated });
    uiState.noteEditing = false;
    uiState.editingNoteContext = null;
    clearNoteEditDraft();
    scheduleRender();
    return;
  }
  if (!saved) {
    // Bridge couldn't resolve a docManager for ctx.docId — note the OLD doc
    // is no longer loaded. Stash for recovery and warn rather than dropping.
    // In collab mode the content is already CRDT-synced into the
    // fragment, so we only need to stash the title (otherwise we'd
    // store a null content and confuse recovery). In legacy mode we
    // stash everything as before.
    stashUnsavedNoteEdit(ctx.docId, ctx.noteId, writeContent
      ? { title, content, updated }
      : { title, updated });
    callbacks.showToast?.(
      t("mobile.toast.noteEditKeptDocGone"),
      { type: "warning" },
    );
  }
  uiState.noteEditing = false;
  uiState.editingNoteContext = null;
  clearNoteEditDraft();
  if (saved) callbacks.saveToLocalStorage?.();
  scheduleRender();
}

function getActiveDocIdForNotes() {
  return (
    callbacks.VaultStorage?.getCurrentDocId?.() ||
    callbacks.Store.get?.("currentDocId") ||
    null
  );
}

// Auto-save the in-progress doc note when the user navigates away (selects a
// different note, switches tabs, etc.) — distinct from cancel-note-edit which
// is an explicit discard. Silent on success; surfaces only failures. Returns
// true when the edit is no longer at risk (written or stashed).
function flushPendingNoteEditSave() {
  const liveDocId = getActiveDocIdForNotes();
  return flushPendingNoteEditSaveTo(liveDocId);
}

// Flush in-progress node edit when the user navigates within the SAME doc
// (tab change, etc.). Targets the live doc, so this only fires successfully
// when the doc hasn't switched. Doc-switch is handled by the variant below
// which targets the OLD doc + stashes on bridge failure.
function flushPendingNodeEditSave() {
  if (!uiState.nodeEditing) return false;
  if (callbacks.Store.isReadOnly?.()) return false;
  snapshotNodeEditDraftFromDom();
  const ctx = uiState.editingNodeContext;
  const liveDocId = getActiveDocIdForNotes();
  if (!ctx || ctx.docId !== liveDocId) return false;
  const nodeId = ctx.nodeId || uiState.activeNodeId;
  if (!nodeId || ctx.nodeId !== nodeId) return false;
  if (!callbacks.Store.find(nodeId)) {
    stashCurrentNodeEditIfDirty(ctx);
    callbacks.showToast?.(
      t("mobile.toast.nodeEditKeptNodeGone"),
      { type: "warning" },
    );
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
    clearNodeEditDraft();
    return true;
  }
  const draft = getNodeEditDraft(ctx) || {};
  const name = draft.name ?? document.getElementById("mobile-node-name")?.value;
  const startDate = draft.startDate ?? document.getElementById("mobile-node-start")?.value ?? null;
  const endDate = draft.endDate ?? document.getElementById("mobile-node-end")?.value ?? null;
  const notes = draft.notes ?? document.getElementById("mobile-node-notes")?.value;
  const baselineColor = ctx.baseline?.color || "";
  const draftColor = draft.color != null ? draft.color : baselineColor;
  const colorChanged = !!draftColor && draftColor !== baselineColor;
  // Only write fields the user could actually have touched (DOM elements
  // exist). The `||"Untitled"` for empty name preserves saveNode behavior.
  if (name == null && notes == null && startDate == null && endDate == null && !colorChanged) {
    return false;
  }
  const updates = {};
  if (name != null) updates.name = name.trim() || t("common.untitled");
  if (startDate !== undefined) updates.startDate = startDate;
  if (endDate !== undefined) updates.endDate = endDate;
  if (notes != null) updates.notes = notes;
  if (colorChanged) updates.color = draftColor;
  try {
    if (colorChanged) {
      callbacks.sharedCallbacks.beginBatch?.();
      try {
        callbacks.sharedCallbacks.onUpdateNode?.(nodeId, updates);
        const liveNode = callbacks.Store.find(nodeId);
        if (liveNode) cascadeNodeColor(liveNode, draftColor);
      } finally {
        callbacks.sharedCallbacks.endBatch?.();
      }
    } else {
      callbacks.sharedCallbacks.onUpdateNode?.(nodeId, updates);
    }
  } catch (err) {
    // Bridge write failed (storage health, etc.). Preserve the content via
    // stash + warn so the user can recover after a refresh / next edit.
    showMobileError(t("mobile.error.saveNodeKept"), err);
    stashUnsavedNodeEdit(ctx.docId, ctx.nodeId, {
      name, startDate, endDate, notes, color: draftColor,
    });
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
    clearNodeEditDraft();
    return false;
  }
  uiState.nodeEditing = false;
  uiState.editingNodeContext = null;
  clearNodeEditDraft();
  callbacks.saveToLocalStorage?.();
  return true;
}

// Variant for the node-edit panel: same doc-switch flush idea. The mobile
// updateNode signature doesn't take a docId — it goes through CRDTBridge
// which writes to the active doc. By the time render() runs after a doc
// switch, the active doc is the NEW one, so a save would land in the wrong
// place. We stash the in-flight values for recovery and surface a warning so
// they can be recovered when the user reopens the original doc.
function flushPendingNodeEditSaveTo(docId) {
  if (!uiState.nodeEditing) return false;
  if (callbacks.Store.isReadOnly?.()) return false;
  snapshotNodeEditDraftFromDom();
  const ctx = uiState.editingNodeContext;
  if (!ctx || !docId || ctx.docId !== docId) return false;
  if (!isNodeEditDirty(ctx)) return false;
  const draft = getNodeEditDraft(ctx) || {};
  const name = draft.name ?? document.getElementById("mobile-node-name")?.value;
  const startDate = draft.startDate ?? document.getElementById("mobile-node-start")?.value;
  const endDate = draft.endDate ?? document.getElementById("mobile-node-end")?.value;
  const notes = draft.notes ?? document.getElementById("mobile-node-notes")?.value;
  const color = draft.color ?? ctx.baseline?.color ?? "";
  if (name == null && startDate == null && endDate == null && notes == null && !color) {
    return false;
  }
  stashUnsavedNodeEdit(ctx.docId, ctx.nodeId, { name, startDate, endDate, notes, color });
  callbacks.showToast?.(
    t("mobile.toast.nodeEditKeptDocGone"),
    { type: "warning" },
  );
  // Mirrors flushPendingNoteEditSaveTo: return true when the edit was
  // safely captured (stashed), even though we couldn't write to the
  // vault directly. Future callers that gate navigation on this return
  // value can treat "stashed" as success.
  return true;
}

// Variant for the doc-switch path: commit the in-progress edit to the doc
// the editor was opened against, even if the live currentDocId has already
// moved on. Critical so a remote sync push that switches docs mid-edit
// doesn't silently drop the user's typing.
//
// CRDTBridge.updateDocNote returns false (does NOT throw) when the old
// doc is no longer accessible via docManager / linkedDocRegistry / shared
// view — the common case after DocumentLoader.handleDocOpen swaps the main
// doc. In that case we stash the unsaved text so the next time the user
// reopens the original doc we can warn them and offer the recovered text.
// Better than silent loss; conservative enough to never auto-write.
function flushPendingNoteEditSaveTo(docId) {
  if (!uiState.noteEditing) return false;
  if (callbacks.Store.isReadOnly?.()) return false;
  snapshotNoteEditDraftFromDom();
  const ctx = uiState.editingNoteContext;
  if (!ctx || !docId || ctx.docId !== docId) {
    // Editor not bound to this doc — nothing to save here.
    return false;
  }
  const titleEl = document.getElementById("mobile-note-title");
  const contentEl = document.getElementById("mobile-note-content");
  const draft = getNoteEditDraft(ctx);
  if (!draft && !titleEl && !contentEl) return false;
  if (!isNoteEditDirty(ctx)) {
    uiState.noteEditing = false;
    uiState.editingNoteContext = null;
    clearNoteEditDraft();
    return false;
  }

  // Collab mode: content is in the fragment, do NOT write it via
  // updateDocNote (that would clear-and-rebuild the fragment and
  // clobber peer edits). Only title flows through this path.
  const collab = !!callbacks.sharedCallbacks.isCollabDocNotesEnabled?.(ctx.docId);
  const writeContent = !collab;
  const title = String(draft?.title ?? titleEl?.value ?? "");
  const content = writeContent
    ? String(draft?.content ?? contentEl?.value ?? "")
    : null;
  const updated = new Date().toISOString();
  let saved = false;
  let kept = false;
  try {
    const updates = noteEditUpdates(ctx.baseline, { title, content }, { collab, updated });
    saved = !!callbacks.sharedCallbacks.updateDocNote?.(
      ctx.noteId,
      updates,
      ctx.docId,
    );
  } catch (err) {
    // Treat throw the same as a false return: stash for recovery.
    console.error("[Mobile] flushPendingNoteEditSaveTo updateDocNote threw:", err);
    saved = false;
  }
  if (!saved) {
    stashUnsavedNoteEdit(ctx.docId, ctx.noteId, writeContent
      ? { title, content, updated }
      : { title, updated });
    kept = true;
    callbacks.showToast?.(
      t("mobile.toast.noteEditKeptDocGone"),
      { type: "warning" },
    );
  }
  uiState.noteEditing = false;
  uiState.editingNoteContext = null;
  clearNoteEditDraft();
  // Only flush vault snapshot when we actually wrote to it.
  if (saved) callbacks.saveToLocalStorage?.();
  return saved || kept;
}

function requestDeleteNote(noteId) {
  if (callbacks.Store.isReadOnly?.()) return;
  if (!noteId) return;
  const liveDocId = getActiveDocIdForNotes();
  const ctx = uiState.editingNoteContext;
  const docId = ctx?.noteId === noteId ? ctx.docId : liveDocId;
  if (!docId) return;
  uiState.confirm = {
    type: "delete-note",
    noteId,
    docId,
    title: t("mobile.confirm.deleteNoteTitle"),
    message: t("mobile.confirm.deleteNote"),
  };
  scheduleRender();
}

function requestDeleteNode(nodeId) {
  // A full map allows deleting, to make room (engine/services/map-size.js).
  if (callbacks.Store.isDeleteBlocked?.()) return;
  const node = callbacks.Store.find(nodeId);
  if (!node) return;
  // A link inside linked content belongs to that map: not deleted from here.
  if (isNestedLink(node)) return;
  if (node.linkedDocId) {
    // Deleting a link only unlinks: the linked map stays in the vault. It
    // writes the open map (allowed even when the linked map is view-only).
    uiState.confirm = {
      type: "unlink-node",
      nodeId,
      title: t("app.unlink.title"),
      message: t("app.unlink.message", { title: node.linkedDocTitle || node.name || t("mobile.confirm.thisNode") }),
    };
    scheduleRender();
    return;
  }
  if (callbacks.sharedCallbacks.isNodeDeleteBlocked?.(nodeId)) return;
  uiState.confirm = {
    type: "delete-node",
    nodeId,
    title: t("mobile.confirm.deleteNodeTitle"),
    message: t("mobile.confirm.deleteNode", { name: node.name || t("mobile.confirm.thisNode") }),
  };
  scheduleRender();
}

function requestDeleteDoc(docId) {
  return docActions.requestDeleteDoc(docId);
}

function requestDeleteFolder(folderId) {
  return docActions.requestDeleteFolder(folderId);
}

async function runConfirmAction() {
  const confirm = uiState.confirm;
  if (!confirm) return;
  try {
    if (confirm.type === "delete-note") {
      deleteNote(confirm.noteId, confirm.docId);
    } else if (confirm.type === "delete-node") {
      deleteNode(confirm.nodeId);
    } else if (confirm.type === "unlink-node") {
      deleteNode(confirm.nodeId);
    } else if (confirm.type === "request-link-access") {
      await callbacks.sharedCallbacks.requestLinkedMapAccess?.(callbacks.Store.find(confirm.nodeId));
    } else if (confirm.type === "delete-doc") {
      await deleteDoc(confirm.docId);
    } else if (confirm.type === "delete-folder") {
      await deleteFolder(confirm.folderId);
    } else if (confirm.type === "recover-note") {
      // Take from stash on accept; cancel leaves the stash for next time.
      const payload = takeStashedNoteEdit(confirm.docId, confirm.noteId);
      // Tag with the original docId/noteId so the render-time consumer can
      // validate that it's still applying to the same edit context. A
      // doc-switch race between confirm-tap and the next render would be
      // caught here.
      applyRecoveredNoteEdit(payload, confirm.docId, confirm.noteId);
    } else if (confirm.type === "recover-node") {
      const payload = takeStashedNodeEdit(confirm.docId, confirm.nodeId);
      applyRecoveredNodeEdit(payload, confirm.docId, confirm.nodeId);
    } else if (confirm.type === "share-remove-member") {
      shareRemoveMember(confirm.userId, confirm.linkOn ? confirm.username : null);
    } else if (confirm.type === "share-restore-access") {
      shareRestoreAccess(confirm.userId, confirm.username);
    } else if (confirm.type === "share-stop-sharing") {
      shareStopSharing();
    } else if (confirm.type === "share-link-renew") {
      shareLinkRenew();
    } else if (confirm.type === "share-link-off") {
      shareLinkTurnOff();
    } else if (confirm.type === "share-link-remove-held") {
      await shareLinkRemoveHeld();
    } else if (confirm.type === "discard-node-edit") {
      closeNodePanelDiscardingDraft();
    } else if (confirm.type === "discard-note-edit") {
      cancelEditNote();
    } else if (typeof confirm.action === "function") {
      await confirm.action();
    }
  } catch (err) {
    showMobileError(t("mobile.error.actionFailed"), err);
  }
  uiState.confirm = null;
  scheduleRender();
}

// Recovery is threaded through render via `uiState.recoveredEditPayload` so
// the next render injects the recovered values as the input default values
// (otherwise the immediate post-confirm render would rewrite the DOM with
// whatever the saved note/node currently shows, throwing away the recovery).
//
// docId/itemId are stored on the payload so the render consumer can verify
// the editor is still bound to the same context. A doc-switch race between
// confirm-tap and render would mismatch and the payload is silently dropped.
function applyRecoveredNoteEdit(payload, docId, noteId) {
  if (!payload) return;
  // Whitelist the expected fields so an old / hand-edited stash entry
  // can't smuggle unrelated keys into uiState via a spread.
  const recoveredTitle = typeof payload.title === "string" ? payload.title : "";
  const recoveredContent = typeof payload.content === "string" ? payload.content : "";

  // Collab mode: the TipTap editor binds to the note's Y.XmlFragment via
  // ySyncPlugin and reads content from there, not from
  // uiState.noteEditDraft. Without this, accepting "Recover unsaved
  // note?" would drop the stashed body silently because the
  // render-time consumer only places it into draft.content (legacy
  // path) and saveNote in collab mode strips content from its
  // update. Route the recovered body through updateDocNote, which in
  // yxml-v1 mode calls setDocNoteFromMarkdown to seed the fragment.
  // This clobbers any concurrent peer edits to the body — that's
  // the user's explicit intent when they tap "Recover" on this
  // confirm prompt. Title still flows through the draft path below.
  const sc = callbacks?.sharedCallbacks;
  const collab = !!sc?.isCollabDocNotesEnabled?.(docId);
  if (collab && recoveredContent && noteId && sc?.updateDocNote) {
    try {
      sc.updateDocNote(noteId, { content: recoveredContent }, docId);
    } catch (err) {
      console.warn("[Mobile] Failed to seed fragment from recovered stash:", err);
    }
  }

  uiState.recoveredEditPayload = {
    kind: "note",
    docId,
    noteId,
    title: recoveredTitle,
    content: recoveredContent,
    updated: typeof payload.updated === "string" ? payload.updated : null,
  };
  scheduleRender();
}

function applyRecoveredNodeEdit(payload, docId, nodeId) {
  if (!payload) return;
  uiState.recoveredEditPayload = {
    kind: "node",
    docId,
    nodeId,
    name: typeof payload.name === "string" ? payload.name : null,
    startDate: typeof payload.startDate === "string" ? payload.startDate : null,
    endDate: typeof payload.endDate === "string" ? payload.endDate : null,
    notes: typeof payload.notes === "string" ? payload.notes : null,
    color: typeof payload.color === "string" ? payload.color : null,
  };
  scheduleRender();
}

function deleteNote(noteId, docId = getActiveDocIdForNotes()) {
  if (!noteId || !docId) return;
  const notes = callbacks.sharedCallbacks.getDocNotes?.(docId) || [];
  if (!notes.some((note) => note.id === noteId)) {
    if (uiState.editingNoteContext?.docId === docId && uiState.editingNoteContext?.noteId === noteId) {
      stashCurrentNoteEditIfDirty(uiState.editingNoteContext);
      uiState.noteEditing = false;
      uiState.editingNoteContext = null;
      clearNoteEditDraft();
      if (uiState.activeNoteId === noteId) uiState.activeNoteId = null;
    }
    callbacks.showToast?.(t("mobile.toast.noteUnavailable"), { type: "warning" });
    return;
  }
  try {
    const removeDocNote = callbacks.sharedCallbacks.removeDocNote;
    if (typeof removeDocNote !== "function") {
      throw new Error("removeDocNote unavailable");
    }
    const removed = removeDocNote(noteId, docId);
    if (!removed) {
      callbacks.showToast?.(t("mobile.toast.noteNotDeleted"), { type: "warning" });
      return;
    }
  } catch (err) {
    showMobileError(t("mobile.error.deleteNote"), err);
    return;
  }
  if (uiState.activeNoteId === noteId) uiState.activeNoteId = null;
  if (uiState.editingNoteContext?.noteId === noteId) {
    uiState.noteEditing = false;
    uiState.editingNoteContext = null;
    clearNoteEditDraft();
  }
}

function deleteNode(nodeId) {
  if (callbacks.Store.isDeleteBlocked?.()) return;
  if (!nodeId) return;
  const state = callbacks.Store.getState();
  if (nodeId === state.data?.id) return;
  try {
    const removed = callbacks.sharedCallbacks.onDeleteNode?.(nodeId);
    if (!removed) {
      callbacks.showToast?.(t("mobile.toast.nodeNotDeleted"), { type: "warning" });
      return;
    }
  } catch (err) {
    showMobileError(t("mobile.error.deleteNode"), err);
    return;
  }
  if (uiState.activeNodeId === nodeId) {
    uiState.activeNodeId = null;
    uiState.nodeEditing = false;
    uiState.editingNodeContext = null;
  }
  callbacks.saveToLocalStorage?.();
}

async function deleteDoc(docId) {
  return docActions.deleteDoc(docId);
}

async function deleteFolder(folderId) {
  return docActions.deleteFolder(folderId);
}

function moveNodeToParent(parentId) {
  if (callbacks.Store.isReadOnly?.()) return;
  const nodeId = uiState.moveNodeId;
  if (!nodeId || !parentId || nodeId === parentId) return;
  const parent = callbacks.Store.find(parentId);
  if (!parent || isDescendantOf(nodeId, parentId)) return;
  const index = parent.children?.length || 0;
  try {
    const moved = callbacks.sharedCallbacks.onMoveNode?.(nodeId, parentId, index);
    if (!moved) {
      callbacks.showToast?.(t("mobile.toast.nodeNotMoved"), { type: "warning" });
      return;
    }
  } catch (err) {
    showMobileError(t("mobile.error.moveNode"), err);
    return;
  }
  uiState.moveNodeId = null;
  uiState.activeNodeId = null;
  callbacks.saveToLocalStorage?.();
}

// Toggle the SUBTREE rooted at the target node — acts on the node itself AND
// on every descendant in one swift action. Complements the per-row left
// chevron, which still toggles only the single node it sits on.
//   - If the target is currently expanded -> collapse it AND fold every
//     descendant
//   - If the target is currently collapsed -> expand it AND unfold every
//     descendant
// (The document root never gets a collapsed=true since it has nowhere to hide.)
function collapseSelectedChildren(nodeId) {
  if (callbacks.Store.isReadOnly?.()) return;
  const state = callbacks.Store.getState();
  const targetId = nodeId || state.selectedId || state.focusId || state.data?.id;
  const target = callbacks.Store.find(targetId);
  if (!target || !target.children?.length) return;

  const isRoot = targetId === state.data?.id;
  const shouldCollapse = !target.collapsed;

  pinScrollToNode(targetId);
  let changed = false;
  callbacks.sharedCallbacks.beginBatch?.();
  try {
    if (shouldCollapse) {
      collapseDescendants(target);
      if (!isRoot) {
        callbacks.sharedCallbacks.onUpdateNode?.(target.id, { collapsed: true });
      }
    } else {
      expandDescendants(target);
      if (!isRoot) {
        callbacks.sharedCallbacks.onUpdateNode?.(target.id, { collapsed: false });
      }
    }
    changed = true;
  } catch (err) {
    showMobileError(t("mobile.error.updateCollapsed"), err);
    return;
  } finally {
    callbacks.sharedCallbacks.endBatch?.();
  }
  if (!changed) return;
  callbacks.saveToLocalStorage?.();
  scheduleRender();
}

// Root (or focused node) of the current map view — fold/unfold act on what
// the user is looking at, not always the document root.
function getFoldScopeNode() {
  const state = callbacks.Store.getState();
  const focus = state.focusId ? callbacks.Store.find(state.focusId) : null;
  return focus || state.data;
}

// Fold/unfold operate ONE LEVEL at a time, not on the whole subtree. The old
// deep-recursive version wrote `collapsed` to every parent at every depth —
// 231 whole-node CRDT rewrites (~116 KB of delta) on a 715-node map, for a
// view that looks identical to collapsing just the first level. Folding only
// the scope's direct children also PRESERVES deeper fold states, so one
// unfold restores the view exactly as it was.

/**
 * Unfold: expand the shallowest visible collapsed level. Repeated taps reveal
 * one more level each time (and progressively clean up documents deep-folded
 * by the old implementation). "Visible" means reachable without passing
 * through a collapsed node — deeper collapsed nodes are left alone.
 */
function expandAllNodes() {
  if (callbacks.Store.isReadOnly?.()) return;
  const scope = getFoldScopeNode();
  if (!scope) return;

  // BFS over visible nodes, collecting collapsed ones level by level; the
  // first level that has any is the one this tap expands.
  let frontier = scope.children || [];
  let targets = [];
  while (frontier.length && !targets.length) {
    targets = frontier.filter((n) => n.children?.length && n.collapsed);
    frontier = frontier.flatMap((n) =>
      n.children?.length && !n.collapsed ? n.children : [],
    );
  }
  if (!targets.length) return;

  callbacks.sharedCallbacks.beginBatch?.();
  try {
    for (const n of targets) {
      callbacks.sharedCallbacks.onUpdateNode?.(n.id, { collapsed: false });
    }
  } catch (err) {
    showMobileError(t("mobile.error.expandNodes"), err);
    return;
  } finally {
    callbacks.sharedCallbacks.endBatch?.();
  }
  callbacks.saveToLocalStorage?.();
  scheduleRender();
  refreshMountedDesktopView();
}

/**
 * Fold: collapse the scope's direct children — one tap back to the overview.
 * Deeper fold states are untouched (and so not re-transmitted).
 */
function collapseAllNodes() {
  if (callbacks.Store.isReadOnly?.()) return;
  const scope = getFoldScopeNode();
  if (!scope) return;
  const targets = (scope.children || []).filter(
    (n) => n.children?.length && !n.collapsed,
  );
  if (!targets.length) return;

  callbacks.sharedCallbacks.beginBatch?.();
  try {
    for (const n of targets) {
      callbacks.sharedCallbacks.onUpdateNode?.(n.id, { collapsed: true });
    }
  } catch (err) {
    showMobileError(t("mobile.error.collapseNodes"), err);
    return;
  } finally {
    callbacks.sharedCallbacks.endBatch?.();
  }
  callbacks.saveToLocalStorage?.();
  scheduleRender();
  refreshMountedDesktopView();
}

// Fold/unfold only touches `collapsed` flags, which never appear in the mobile
// shell markup — the byte-identical skip in render() then never remounts the
// embedded view, and the desktop batch-render listener skips entirely while
// the shell is visible. Repaint the mounted view directly so the change is
// visible immediately, then re-fit: folding/unfolding changes the layout
// extent drastically (a fold can take a mindmap from ~23000px tall to a
// screenful), and the old framing would show a cropped or empty region.
function refreshMountedDesktopView() {
  const name = getMountedDesktopViewName();
  if (name) {
    callbacks.renderDesktopView?.(name);
    // Double-rAF: let the fresh render lay out (and bubble physics restore
    // stamped positions) before measuring for the fit.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (getMountedDesktopViewName() === name) fitMountedDesktopView();
    }));
    // Bubble folds change radii, so the physics simulation is still settling
    // when the first fit measures — re-frame once it has had time to converge.
    setTimeout(() => {
      if (getMountedDesktopViewName() === name) fitMountedDesktopView();
    }, 2500);
  }
}

function toggleNodeTagFromButton(actionEl) {
  const nodeId = actionEl.dataset.nodeId;
  const tagId = actionEl.dataset.tagId;
  if (!nodeId || !tagId) return;
  if (actionEl.dataset.tagScope === "status") {
    setExclusiveNodeStatus(nodeId, tagId);
    scheduleRender();
    return;
  }
  try {
    callbacks.sharedCallbacks.toggleNodeTag?.(nodeId, tagId);
  } catch (err) {
    showMobileError(t("mobile.error.updateTag"), err);
    return;
  }
  callbacks.saveToLocalStorage?.();
  scheduleRender();
}

function setExclusiveNodeStatus(nodeId, tagId, sourceTagId = null) {
  if (callbacks.Store.isReadOnly?.()) return;
  const node = callbacks.Store.find(nodeId);
  if (!node || !tagId) return;
  const tagDefs = getTagDefinitions(nodeId);
  const tagMap = new Map(tagDefs.map((tag) => [tag.id, tag]));
  const columnTagIds = getBoardColumns(tagDefs).map((col) => col.tagId);
  const currentIds = getNodeTagIds(node);
  const sourceIds = sourceTagId
    ? [sourceTagId]
    : columnTagIds.filter((id) => id !== tagId && currentIds.includes(id));

  if (currentIds.includes(tagId) && sourceIds.length === 0) return;

  callbacks.sharedCallbacks.beginBatch?.();
  try {
    if (!currentIds.includes(tagId)) {
      callbacks.sharedCallbacks.toggleNodeTag?.(nodeId, tagId);
    }
    for (const sourceId of sourceIds) {
      if (!sourceId || sourceId === tagId) continue;
      const sourceTag = tagMap.get(sourceId);
      const targetTag = tagMap.get(tagId);
      if (sourceTag?.mandatory && targetTag?.mandatory) continue;
      const fresh = callbacks.Store.find(nodeId) || node;
      if (nodeHasTagId(fresh, sourceId) || currentIds.includes(sourceId)) {
        callbacks.sharedCallbacks.toggleNodeTag?.(nodeId, sourceId);
      }
    }
  } catch (err) {
    showMobileError(t("mobile.error.changeStatus"), err);
    return;
  } finally {
    callbacks.sharedCallbacks.endBatch?.();
  }
  callbacks.saveToLocalStorage?.();
}

function setCardStatus(nodeId, tagId, sourceTagId = null) {
  setExclusiveNodeStatus(nodeId, tagId, sourceTagId);
  uiState.statusNodeId = null;
  uiState.statusSourceTagId = null;
  scheduleRender();
}

async function moveDocToFolder(folderId) {
  return docActions.moveDocToFolder(folderId);
}

function toggleStoreIdList(key, id) {
  if (!id) return;
  const current = callbacks.Store.get(key) || [];
  const next = current.includes(id)
    ? current.filter((item) => item !== id)
    : [...current, id];
  if (key === "highlightedTagIds") callbacks.Store.setHighlightedTagIds(next);
  if (key === "hiddenTagIds") callbacks.Store.setHiddenTagIds(next);
}

function showMobileError(message, err) {
  console.error(`[Mobile] ${message}:`, err);
  callbacks.showToast?.(t("mobile.error.detail", { message, detail: err?.message || t("vault.error.unknown") }), { type: "error" });
}

function findDoc(docId) {
  return docActions.findDoc(docId);
}

function buildFolderTrail(folders, activeFolderId) {
  return docActions.buildFolderTrail(folders, activeFolderId);
}

function findFolder(folderId) {
  return docActions.findFolder(folderId);
}

function getCurrentDocTitle(appState) {
  return docActions.getCurrentDocTitle(appState);
}

function getRootFolderId(index) {
  return docActions.getRootFolderId(index);
}

function getActiveDocsFolderId(index) {
  return docActions.getActiveDocsFolderId(index);
}

function normalizeFolderId(folderId, rootFolderId) {
  return docActions.normalizeFolderId(folderId, rootFolderId);
}

function isOwnerDoc(doc) {
  return docActions.isOwnerDoc(doc);
}

function ensureOwnerDocAction(docId, action) {
  return docActions.ensureOwnerDocAction(docId, action);
}

function docMatchesQuery(doc, query) {
  return docActions.docMatchesQuery(doc, query);
}

function navigateUpFolder() {
  return docActions.navigateUpFolder();
}

function getSearchResults(query, appState) {
  const q = query.trim().toLowerCase();
  if (!q) return { nodes: [], notes: [] };
  const nodes = (callbacks.Store.getAll?.() || [])
    .filter((node) => {
      const name = (node.name || "").toLowerCase();
      const notes = String(node.notes || "").toLowerCase();
      return name.includes(q) || notes.includes(q);
    })
    .slice(0, 30);
  const notes = (callbacks.sharedCallbacks.getDocNotes?.() || [])
    .filter((note) => {
      const title = (note.title || "").toLowerCase();
      const content = readSearchableNote(note, callbacks.sharedCallbacks).toLowerCase();
      return title.includes(q) || content.includes(q);
    })
    .slice(0, 20);
  return { nodes, notes };
}

function collectFolderEntries(index) {
  return docActions.collectFolderEntries(index);
}

function getMoveSuggestions(moving, appState) {
  const candidates = [];
  const addCandidate = (label, node) => {
    if (!node || node.id === moving.id || isDescendantOf(moving.id, node.id)) return;
    if (candidates.some((item) => item.node.id === node.id)) return;
    candidates.push({ label, node });
  };

  addCandidate(t("mobile.move.docRoot"), appState.data);
  addCandidate(t("mobile.move.currentFocus"), callbacks.Store.find(appState.focusId));
  addCandidate(t("mobile.move.selected"), callbacks.Store.find(appState.selectedId));
  addCandidate(t("mobile.move.currentParent"), callbacks.Store.findParent?.(moving.id));
  return candidates.slice(0, 4);
}

function isDescendantOf(ancestorId, nodeId) {
  let current = callbacks.Store.findParent?.(nodeId);
  while (current) {
    if (current.id === ancestorId) return true;
    current = callbacks.Store.findParent?.(current.id);
  }
  return false;
}

function getTagDefinitions(nodeId = null) {
  return callbacks.sharedCallbacks.getTagDefinitions?.(nodeId) || [];
}

function getNodeTagIds(node) {
  return (node.tags || [])
    .map((tag) => typeof tag === "string" ? tag : tag?.tagId)
    .filter(Boolean);
}

function nodeHasTagId(node, tagId) {
  return getNodeTagIds(node).includes(tagId);
}

function getNodeTagLabels(node) {
  const defs = getTagDefinitions(node.id);
  const tagMap = new Map(defs.map((tag) => [tag.id, tag]));
  return getNodeTagIds(node)
    .map((tagId) => tagMap.get(tagId))
    .filter(Boolean);
}

function renderTagChip(tag) {
  return `<button class="mobile-tag-chip"
                  data-mobile-action="quick-filter-tag"
                  data-tag-id="${escapeAttr(tag.id)}"
                  title="${escapeAttr(t("mobile.tag.filterBy", { name: tagLabel(tag) }))}">${escapeHtml(tagLabel(tag))}</button>`;
}

function renderToggleTag(tag, active, nodeId, scope = "tag") {
  return `
    <button class="mobile-tag-toggle ${active ? "active" : ""}"
            data-mobile-action="toggle-node-tag"
            data-node-id="${escapeAttr(nodeId)}"
            data-tag-id="${escapeAttr(tag.id)}"
            data-tag-scope="${escapeAttr(scope)}">
      ${escapeHtml(tagLabel(tag))}
    </button>
  `;
}

function getEffectiveStatus(node) {
  return callbacks.sharedCallbacks.getEffectiveStatus?.(node) || node.status || "todo";
}

function getSafeStatus(node) {
  const status = getEffectiveStatus(node);
  return ["todo", "in-progress", "done"].includes(status) ? status : "todo";
}

// Hex-only allowlist to keep the color picker from accidentally surfacing
// CSS keywords or arbitrary strings the host theme might emit. Mirrors the
// safety filter the desktop modal-manager applies to its swatches.
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{3,8}$/;

function getNodeColorPalette() {
  const raw = callbacks.getColors?.() || [];
  return raw.filter((c) => typeof c === "string" && HEX_COLOR_RE.test(c));
}

function getBoardColumns(tagDefs) {
  const configured = callbacks.sharedCallbacks.getKanbanColumns?.() || [];
  if (configured.length) return configured;
  return tagDefs
    .filter((tag) => tag.mandatory)
    .map((tag, order) => ({ tagId: tag.id, order }));
}
